/**
 * Dual Analytics Event Tracking (GA4 + PostHog)
 *
 * Usage:
 * import { trackEvent } from '@/lib/analytics'
 * trackEvent('grass_id_result_rendered', { grass_type: 'Kentucky Bluegrass', raw_confidence: 0.95 })
 */

import posthog from 'posthog-js'
import {
  analysisFunnelEnvelope as baseAnalysisFunnelEnvelope,
  type AnalysisFunnelEnvelopeInput,
  type AnalysisFunnelEnvelopeProperties,
  type AnalysisFunnelMode,
} from '@/lib/analysis-funnel-envelope'
import {
  currentFromTool,
  currentRouteSurface,
  resolveFunnelSource,
  sessionEntryPathname,
  sessionEntrySurface,
} from '@/lib/analytics/entry-source'
import type { AnalyticsUserTier } from '@/lib/analytics/user-tier'
import {
  stashCommunityDiscoveryAttribution,
  type CommunityDiscoveryContext,
  type CommunityDiscoverySource,
} from '@/lib/community/discovery'
import { markNewsletterSubscribed } from '@/lib/usage-tracker'

export {
  type AnalysisFunnelEnvelopeInput,
  type AnalysisFunnelEnvelopeProperties,
  type AnalysisFunnelMode,
} from '@/lib/analysis-funnel-envelope'

export {
  currentRouteSurface,
  resolveFunnelSource,
  sessionEntrySurface,
  surfaceFromPathname,
} from '@/lib/analytics/entry-source'
export {
  anonymousLimitImpliesEmailTier,
  markEmailUnlockHint,
  resolveAnalyticsUserTier,
  type AnalyticsUserTier,
} from '@/lib/analytics/user-tier'

/**
 * Ambient tier for every funnel envelope emitted from this page.
 *
 * The tier is known once, in the auth-aware host (every scan flow calls
 * `useAnalyticsUserTier`, which publishes it whenever auth or subscription
 * state settles — a flow that skips it inherits the previous flow's), while the
 * envelope is built in thirty places that have no access to it. Setting it once
 * here is what stops `user_tier` from being another field that is null on 100%
 * of rows. Any call site that DOES know better still wins: an explicit
 * `userTier` on the envelope input overrides this.
 */
let ambientUserTier: AnalyticsUserTier | null = null

export function setAnalyticsUserTier(tier: AnalyticsUserTier | null): void {
  ambientUserTier = tier
}

export function getAnalyticsUserTier(): AnalyticsUserTier | null {
  return ambientUserTier
}

/**
 * Browser-side funnel envelope: the shared join contract plus the entry
 * attribution and tier dimension that only a browser can derive.
 *
 * `source` is optional here (it was a required literal that every caller set to
 * 'homepage'); omit it and it resolves to the real route the flow is running
 * on. See the property-split note in lib/analytics/entry-source.ts for why
 * `source` was kept rather than replaced.
 *
 * Server callers must keep importing `analysisFunnelEnvelope` from
 * '@/lib/analysis-funnel-envelope' directly — that module stays browser-free.
 */
export function analysisFunnelEnvelope(
  input: Omit<AnalysisFunnelEnvelopeInput, 'source'> & { source?: string },
): AnalysisFunnelEnvelopeProperties {
  return baseAnalysisFunnelEnvelope({
    ...input,
    source: resolveFunnelSource(input.source),
    entrySurface: input.entrySurface ?? sessionEntrySurface(),
    entryPathname: input.entryPathname ?? sessionEntryPathname(),
    routeSurface: input.routeSurface ?? currentRouteSurface(),
    fromTool: input.fromTool ?? currentFromTool(),
    userTier: input.userTier ?? ambientUserTier,
  })
}

export type EventProperties = Record<string, string | number | boolean | null | undefined>
const EMAIL_CAPTURE_PENDING_ATTRIBUTION_KEY = 'email_capture_pending_attribution'
const EMAIL_CAPTURE_PENDING_ATTRIBUTION_TTL_MS = 5 * 60 * 1000
// Read (and cleared) server-side by app/auth/callback/route.ts — keep in sync.
export const EMAIL_CAPTURE_ATTRIBUTION_COOKIE = 'ph_capture_attr'

export interface PendingEmailCaptureAttribution {
  source: string
  ts: number
}

export function stashEmailCaptureAttribution(source: string) {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(
      EMAIL_CAPTURE_PENDING_ATTRIBUTION_KEY,
      JSON.stringify({ source, ts: Date.now() }),
    )
  } catch {
    // localStorage may be unavailable in private browsing. Attribution is best-effort.
  }
  try {
    if (typeof document === 'undefined') return
    // Mirror the stash into a cookie so the server-side OAuth callback (which
    // fires the reliable signup_completed) can attribute the signup source.
    // SameSite=Lax: the OAuth return is a top-level GET navigation, Strict
    // would drop the cookie. Deliberately not HttpOnly (set from JS,
    // non-sensitive). max-age matches EMAIL_CAPTURE_PENDING_ATTRIBUTION_TTL_MS.
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${EMAIL_CAPTURE_ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify({ source, ts: Date.now() }))}` +
      `; path=/; max-age=${Math.floor(EMAIL_CAPTURE_PENDING_ATTRIBUTION_TTL_MS / 1000)}; SameSite=Lax${secure}`
  } catch {
    // Cookie write is best-effort attribution too.
  }
}

/**
 * The browser's PostHog distinct id, for threading into API requests so
 * server-side captures land on the same person as the client funnel events.
 * Analytics only — never trust this server-side for quota or rate limiting.
 */
export function getPosthogDistinctId(): string | undefined {
  try {
    const id = posthog.get_distinct_id()
    return typeof id === 'string' && id ? id : undefined
  } catch {
    return undefined
  }
}

/**
 * The active PostHog session id, paired with the distinct id on critical
 * server boundaries so server captures remain attached to the browser replay.
 */
export function getPosthogSessionId(): string | undefined {
  try {
    const id = posthog.get_session_id()
    return typeof id === 'string' && id ? id : undefined
  } catch {
    return undefined
  }
}

/**
 * First-touch attribution (2026-08-28 tracking audit P0-3).
 *
 * Snapshotted into localStorage on the visitor's FIRST page load and stamped
 * as `$set_once` person properties when the person is eventually identified
 * (person_profiles is 'identified_only', so anonymous visitors have no person
 * to stamp — the localStorage carry is what survives until sign-up, however
 * many sessions later). PERISHABLE: none of this can be backfilled, which is
 * why it ships before the March traffic ramp. Key names are mirrored verbatim
 * in the mobile envelope so cross-platform cohorts join.
 */
const FIRST_TOUCH_KEY = 'wgit_first_touch'

export interface FirstTouchSnapshot {
  first_landing_path: string
  first_referrer_domain: string
  first_utm_source: string | null
  first_utm_medium: string | null
  first_utm_campaign: string | null
  first_entry_surface: string
  first_platform: 'web'
  first_seen_at: string
  /** Acquisition channel of the first visit; see classifyFirstChannel. */
  first_channel: FirstChannel
}

export type FirstChannel =
  | 'ai'
  | 'paid'
  | 'email'
  | 'organic_search'
  | 'social'
  | 'referral'
  | 'direct'

// Hostnames match exactly or as a parent domain (www.perplexity.ai matches
// perplexity.ai). Bing's chat lives on www.bing.com itself, which a hostname
// cannot tell apart from Bing search; only the Edge sidebar host is separable.
const AI_REFERRER_DOMAINS = [
  'chatgpt.com',
  'chat.openai.com',
  'perplexity.ai',
  'gemini.google.com',
  'bard.google.com',
  'copilot.microsoft.com',
  'copilot.cloud.microsoft',
  'edgeservices.bing.com',
  'claude.ai',
  'duck.ai',
  'you.com',
  'grok.com',
  'chat.deepseek.com',
  'meta.ai',
  'chat.mistral.ai',
]
const SOCIAL_REFERRER_DOMAINS = [
  'facebook.com',
  'fb.com',
  'instagram.com',
  'threads.net',
  't.co',
  'twitter.com',
  'x.com',
  'reddit.com',
  'youtube.com',
  'tiktok.com',
  'linkedin.com',
  'lnkd.in',
  'nextdoor.com',
  'quora.com',
  'bsky.app',
]
const WEBMAIL_REFERRER_DOMAINS = [
  'mail.google.com',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.yahoo.com',
  'mail.aol.com',
  'icloud.com',
  // Android app referrers arrive as android-app://<package>/.
  'com.google.android.gm',
]
const SEARCH_REFERRER_DOMAINS = [
  'bing.com',
  'duckduckgo.com',
  'search.yahoo.com',
  'yahoo.com',
  'ecosia.org',
  'search.brave.com',
  'startpage.com',
  'qwant.com',
  'baidu.com',
  'naver.com',
  'aol.com',
  'ask.com',
  'com.google.android.googlequicksearchbox',
]

function hostMatches(host: string, domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`))
}

/**
 * First-touch acquisition channel from the landing referrer and UTM tags.
 * Order matters: explicit email/paid tags beat the referrer, AI beats search
 * (gemini.google.com is a Google host), and our own host counts as direct.
 * ChatGPT tags outbound links `utm_source=chatgpt.com`, so AI is matched on
 * utm_source as well as on the referrer.
 */
export function classifyFirstChannel(input: {
  referrerDomain: string
  utmSource?: string | null
  utmMedium?: string | null
  ownHost?: string
}): FirstChannel {
  const host = (input.referrerDomain || '').toLowerCase().replace(/\.$/, '')
  const source = (input.utmSource || '').toLowerCase()
  const medium = (input.utmMedium || '').toLowerCase()
  const ownHost = (input.ownHost || '').toLowerCase().replace(/^www\./, '')

  if (medium === 'email' || medium === 'e-mail' || medium === 'newsletter') return 'email'
  if (/^(cpc|ppc|cpm|paid|paid[_-]?\w+|display|retargeting)$/.test(medium)) return 'paid'
  if (hostMatches(host, AI_REFERRER_DOMAINS) || hostMatches(source, AI_REFERRER_DOMAINS)) {
    return 'ai'
  }
  if (medium === 'social' || medium === 'social-media' || medium === 'social_media') return 'social'
  if (medium === 'organic') return 'organic_search'

  const isDirect = !host || host === '$direct' || (ownHost !== '' && hostMatches(host, [ownHost]))
  if (isDirect) return 'direct'
  if (hostMatches(host, WEBMAIL_REFERRER_DOMAINS)) return 'email'
  if (hostMatches(host, SOCIAL_REFERRER_DOMAINS) || /(^|\.)pinterest\.[a-z.]+$/.test(host)) {
    return 'social'
  }
  if (/(^|\.)google\.[a-z.]+$/.test(host) || /(^|\.)yandex\.[a-z.]+$/.test(host)) {
    return 'organic_search'
  }
  if (hostMatches(host, SEARCH_REFERRER_DOMAINS)) return 'organic_search'
  return 'referral'
}

function entrySurfaceForPath(path: string): string {
  if (path === '/') return 'homepage'
  if (path.startsWith('/blog')) return 'blog'
  if (path.startsWith('/guides')) return 'guide'
  if (path.startsWith('/tools')) return 'tool'
  if (path.startsWith('/compare')) return 'compare'
  if (path.startsWith('/diagnose')) return 'diagnose'
  if (path.startsWith('/weed')) return 'weed'
  if (path.startsWith('/result')) return 'shared_result'
  return 'other'
}

/**
 * Write the snapshot if this browser has none. Safe to call on every load —
 * only the first ever wins. Called from the analytics bootstrap in
 * app/providers.tsx so it runs before any identify can.
 */
export function captureFirstTouchSnapshot(): void {
  if (typeof window === 'undefined') return
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return
    const url = new URL(window.location.href)
    let referrerDomain = ''
    try {
      referrerDomain = document.referrer ? new URL(document.referrer).hostname : ''
    } catch {
      referrerDomain = ''
    }
    const utmSource = url.searchParams.get('utm_source')
    const utmMedium = url.searchParams.get('utm_medium')
    const snapshot: FirstTouchSnapshot = {
      first_landing_path: url.pathname,
      first_referrer_domain: referrerDomain || '$direct',
      first_utm_source: utmSource,
      first_utm_medium: utmMedium,
      first_utm_campaign: url.searchParams.get('utm_campaign'),
      first_entry_surface: entrySurfaceForPath(url.pathname),
      first_platform: 'web',
      first_seen_at: new Date().toISOString(),
      first_channel: classifyFirstChannel({
        referrerDomain,
        utmSource,
        utmMedium,
        ownHost: url.hostname,
      }),
    }
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(snapshot))
  } catch {
    // Storage unavailable (private mode, blocked) — first-touch is best-effort.
  }
}

/** The stored snapshot, shaped for posthog.identify's $set_once argument. */
export function firstTouchSetOnce(): FirstTouchSnapshot | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)
    // Shape check, not just typeof: an array or foreign object parses fine and
    // would both suppress capture and stamp garbage (Codex review 2026-08-28).
    const valid =
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      typeof parsed.first_landing_path === 'string' &&
      typeof parsed.first_seen_at === 'string'
    if (!valid) return undefined
    const snapshot = parsed as FirstTouchSnapshot
    // Snapshots written before first_channel existed still hold the referrer
    // domain and UTM tags it is derived from.
    if (typeof snapshot.first_channel !== 'string') {
      snapshot.first_channel = classifyFirstChannel({
        referrerDomain:
          typeof snapshot.first_referrer_domain === 'string' ? snapshot.first_referrer_domain : '',
        utmSource: snapshot.first_utm_source,
        utmMedium: snapshot.first_utm_medium,
        ownHost: window.location.hostname,
      })
    }
    return snapshot
  } catch {
    return undefined
  }
}

/**
 * Owner/QA traffic flag. Visiting any page with `?wgis_internal=1` marks this
 * browser internal until `?wgis_internal=0`; the state lives in a first-party
 * cookie (two years) with the older localStorage key as a fallback read. The
 * caller registers `is_internal: true` as a PostHog super property, which the
 * project's test-account filter (`is_internal is_not true`) excludes. The
 * parameter carries no personal data by design; never add any to it.
 */
export const INTERNAL_TRAFFIC_KEY = 'wgis_internal'
const INTERNAL_TRAFFIC_MAX_AGE_S = 60 * 60 * 24 * 730

function readCookie(name: string): string | null {
  try {
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`))
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null
  } catch {
    return null
  }
}

/**
 * Apply a `wgis_internal` query parameter, if present, and return whether this
 * browser is internal traffic.
 */
export function resolveInternalTrafficFlag(search: string = typeof window !== 'undefined' ? window.location.search : ''): boolean {
  if (typeof document === 'undefined') return false
  let param: string | null = null
  try {
    param = new URLSearchParams(search).get(INTERNAL_TRAFFIC_KEY)
  } catch {
    param = null
  }
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : ''

  if (param === '1') {
    document.cookie = `${INTERNAL_TRAFFIC_KEY}=1; path=/; max-age=${INTERNAL_TRAFFIC_MAX_AGE_S}; SameSite=Lax${secure}`
    try {
      localStorage.setItem(INTERNAL_TRAFFIC_KEY, '1')
    } catch {
      // Cookie alone is enough.
    }
    return true
  }
  if (param === '0') {
    document.cookie = `${INTERNAL_TRAFFIC_KEY}=; path=/; max-age=0; SameSite=Lax${secure}`
    try {
      localStorage.removeItem(INTERNAL_TRAFFIC_KEY)
    } catch {
      // Nothing stored.
    }
    return false
  }

  if (readCookie(INTERNAL_TRAFFIC_KEY) === '1') return true
  try {
    return localStorage.getItem(INTERNAL_TRAFFIC_KEY) === '1'
  } catch {
    return false
  }
}

// Read (and cleared) server-side by app/auth/callback/route.ts — keep in sync.
export const PH_OAUTH_IDENTITY_COOKIE = 'ph_oauth_identity'
// Bounded to a practical OAuth round trip. Anything older is a stale tab, not
// a live redirect, so it must not attribute the callback it happens to meet.
const PH_OAUTH_IDENTITY_TTL_MS = 10 * 60 * 1000

/**
 * Whether posthog-js already identified this browser id (a previous sign-in on
 * this device). Threaded to the callback so it can skip `client.alias()`:
 * aliasing an already-identified id permanently fuses two real people.
 * `_isIdentified()` is posthog-js's own accessor — private by name, present in
 * the installed typings; `$user_state` is the property it derives from and is
 * the fallback for builds that drop the method.
 */
function isPosthogIdentified(): boolean {
  try {
    const client = posthog as unknown as {
      _isIdentified?: () => boolean
      get_property?: (property: string) => unknown
    }
    if (typeof client._isIdentified === 'function') return client._isIdentified() === true
    if (typeof client.get_property === 'function') {
      return client.get_property('$user_state') === 'identified'
    }
    return false
  } catch {
    return false
  }
}

function oauthIdentityNonce(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch {
    // Fall through to the Math.random form.
  }
  return `on_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Mirror the browser's PostHog distinct id and session id into a short-lived
 * cookie immediately before an OAuth redirect. The provider returns via a
 * top-level GET navigation, so the callback route cannot receive the
 * X-POSTHOG-* headers lib/claim-analysis.ts uses; a cookie is the only channel
 * that survives. Without it every server-fired signup_completed lands with a
 * null $session_id and on a distinct id the browser stream has never seen.
 *
 * ANALYTICS ONLY. This cookie is written by client JS, is deliberately not
 * HttpOnly, and is trivially forgeable from a devtools console. It must NEVER
 * gate auth, quota, entitlement, or any other server decision — its only
 * legitimate consumer is PostHog attribution in app/auth/callback/route.ts.
 *
 * The `nonce` binds the payload to one redirect only partially, and saying so
 * precisely matters. It travels in the cookie itself, the same channel the
 * callback reads, so it cannot prove the cookie belongs to THIS transaction the
 * way an OAuth `state` round trip would. What the design does achieve: every
 * redirect rewrites the cookie (last write wins, so a second tab cannot leave
 * the first tab's stash in place), the callback deletes it on every outcome
 * including the failure redirects, and the freshness window above bounds reuse
 * — together those mean one stash attributes at most one callback and only
 * within the round-trip window. What it does NOT achieve: any defense against a
 * forged or replayed value. That is acceptable precisely because nothing but
 * attribution reads it.
 */
export function stashPosthogIdentityForOAuth() {
  try {
    if (typeof document === 'undefined') return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    const distinctId = getPosthogDistinctId()
    const sessionId = getPosthogSessionId()
    if (!distinctId && !sessionId) {
      // Returning early would leave an earlier redirect's cookie in place to
      // attribute this one. Clear it instead.
      document.cookie = `${PH_OAUTH_IDENTITY_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`
      return
    }
    const payload = encodeURIComponent(
      JSON.stringify({
        did: distinctId,
        sid: sessionId,
        idf: isPosthogIdentified(),
        nonce: oauthIdentityNonce(),
        ts: Date.now(),
      }),
    )
    document.cookie =
      `${PH_OAUTH_IDENTITY_COOKIE}=${payload}` +
      `; path=/; max-age=${Math.floor(PH_OAUTH_IDENTITY_TTL_MS / 1000)}; SameSite=Lax${secure}`
  } catch {
    // Attribution is best-effort and must never block the OAuth redirect.
  }
}

export function takeEmailCaptureAttribution(now = Date.now()): PendingEmailCaptureAttribution | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(EMAIL_CAPTURE_PENDING_ATTRIBUTION_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<PendingEmailCaptureAttribution>
    if (
      typeof parsed.source === 'string' &&
      typeof parsed.ts === 'number' &&
      now - parsed.ts <= EMAIL_CAPTURE_PENDING_ATTRIBUTION_TTL_MS
    ) {
      return { source: parsed.source, ts: parsed.ts }
    }
    return null
  } catch {
    return null
  } finally {
    try {
      window.localStorage.removeItem(EMAIL_CAPTURE_PENDING_ATTRIBUTION_KEY)
    } catch {
      // ignore
    }
  }
}

type ClientErrorSource =
  | 'react_boundary'
  | 'window'
  | 'promise'
  | 'network'
  | 'api'
  | 'calculator'
  | 'google-maps-load'
  | 'google-maps-auth'
  | 'places-autocomplete'
  | 'places-details'
  | 'google-maps-drawing'

export type CalculatorType =
  | 'fertilizer'
  | 'seeding'
  | 'watering'
  | 'mowing'
  | 'aeration'
  | 'herbicide'
  | 'cost'
  | 'soil-temperature'
  | 'lawn-size-calculator'
  | 'grass-comparison'
  | 'soil-test-analyzer'
  | 'tank-mix'
  | 'fertilizer-rate'
  // Intentionally shared by ~10 branded product calculator routes (milorganite,
  // scotts-*, pgf-complete, lesco, ironite, simple-lawn-solutions) which all
  // render the same component. Per-product breakdowns must slice on the
  // `product` input property, NOT on calculator_type.
  | 'fertilizer-schedule'
  | 'sprinkler-cost'
  | 'weed-and-feed'
  | 'overseeding'
  | 'sod'
  | 'topsoil'
  | 'lime'
  | 'grub-control'
  | 'artificial-turf-cost'
  | 'dethatching'
  | 'spreader-settings'

/**
 * `calculator_type` reached PostHog in two spellings for the same calculator:
 * the bare form (`seeding`) and the tool-slug form (`seeding-calculator`).
 * Split series cannot be summed after the fact, so emitters normalise to the
 * BARE form and ship the pre-normalisation value as `calculator_type_raw`.
 * History stays readable: a query that needs the old split reads the raw
 * property, everything else reads the canonical one.
 */
export const CALCULATOR_TYPE_ALIASES: Record<string, string> = {
  'seeding-calculator': 'seeding',
  'fertilizer-calculator': 'fertilizer',
  'watering-schedule': 'watering',
  'top-dressing-calculator': 'top-dressing',
}

/** Canonical `calculator_type` for a raw slug. Unknown values pass through. */
export function normalizeCalculatorType(raw: string): string {
  return CALCULATOR_TYPE_ALIASES[raw] ?? raw
}

/**
 * The canonical + raw property pair every `calculator_type` emitter sends.
 * `calculator_type_raw` is always present so a reader never has to guess
 * whether an event predates normalisation.
 */
export function calculatorTypeProps(raw: string): EventProperties {
  return {
    calculator_type: normalizeCalculatorType(raw),
    calculator_type_raw: raw,
  }
}

export type CalculatorPayload = {
  inputs: Record<string, string | number | boolean | string[] | undefined>
  result?: Record<string, string | number | boolean | undefined>
  userTier?: 'free' | 'premium'
  location?: string
  source?: 'form_submit' | 'live_update' | 'url_params' | 'saved_profile' | 'quick_start'
}

const ASIN_PATTERNS = [
  /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /[?&]asin=([A-Z0-9]{10})(?:&|$)/i,
]

export const extractAsin = (url: string | null | undefined): string | null => {
  if (!url) return null
  for (const pattern of ASIN_PATTERNS) {
    const match = url.match(pattern)
    if (match) return match[1].toUpperCase()
  }
  return null
}

/**
 * Guarantee the gtag command queue exists even before gtag.js has loaded, so
 * events fired in the pre-load window queue into `dataLayer` and drain once the
 * (proxied) gtag.js arrives — instead of being silently dropped. Mirrors the inline
 * bootstrap in components/analytics/GoogleAnalytics.tsx and is idempotent with it.
 *
 * Returns null when GA isn't configured (no measurement ID). Without that guard we'd
 * build a dataLayer queue that never drains — an unbounded in-memory leak.
 */
function ensureGtag(): Window['gtag'] | null {
  if (typeof window === 'undefined') return null
  if (!process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) return null
  const w = window as Window & { dataLayer?: unknown[] }
  if (!w.gtag) {
    w.dataLayer = w.dataLayer || []
    // Push the raw `arguments` object (matches gtag's own queue format) so gtag.js
    // processes these once it loads and replaces window.gtag with the real impl.
    w.gtag = function gtag() {
      w.dataLayer!.push(arguments)
    } as Window['gtag']
  }
  return w.gtag ?? null
}

/**
 * The three affiliate click events. They stay separate forever: their keys and
 * history are load-bearing, and several surfaces deliberately emit two of them
 * for a single click (product-pick-card, product-recommendations,
 * diagnosis-product-picks). Do NOT rename or merge them, and do NOT union them
 * by raw count — that doubles ~95% of clicks. Union on `click_family` and count
 * DISTINCT `click_id` instead.
 */
const AFFILIATE_CLICK_EVENTS = new Set([
  'affiliate_link_clicked',
  'affiliate_product_clicked',
  'calculator_product_clicked',
])

/**
 * Affiliate clicks open Amazon in a new tab. On mobile the origin tab is
 * backgrounded (not unloaded), so the batched queue's timer can be suspended
 * before it flushes; these events bypass the queue.
 */
const INSTANT_SEND_EVENTS = new Set([
  'affiliate_link_clicked',
  'affiliate_product_clicked',
])

/**
 * Per-click id shared by every affiliate event emitted in one synchronous click
 * handler, released on the next microtask. Same synchronous-scope contract as
 * withResultCtaLocation below: sound only because all of these emitters fire
 * synchronously from the click handler. Two real user clicks can never land in
 * one microtask, so ids cannot collide across genuine clicks.
 *
 * The contract is a convention, not something the runtime can enforce, and it
 * breaks in exactly two ways:
 *  - An `await` (or setTimeout/promise callback) BETWEEN two emitters that are
 *    meant to describe ONE click splits them across microtasks. They get two
 *    ids, and the DISTINCT-click_id CTR counts that click twice.
 *  - Nested synchronous programmatic clicks — a handler that calls into another
 *    surface's emitter before returning — never yield, so both collapse onto
 *    ONE id and two genuine clicks count as one.
 * __tests__/lib/affiliate-click-join-keys.test.ts pins the nested case, so
 * changing this scope is a conscious decision rather than a silent one.
 */
let currentAffiliateClickId: string | null = null

function affiliateClickId(): string {
  if (currentAffiliateClickId) return currentAffiliateClickId
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `ac_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  currentAffiliateClickId = id
  queueMicrotask(() => {
    currentAffiliateClickId = null
  })
  return id
}

/**
 * Additive join keys for the affiliate click family:
 *  - `click_family: 'affiliate'` — the single union filter
 *  - `placement_key` — one taxonomy, coalesced from the three legacy property
 *    names (`surface` | `placement` | `tracking_context`). Existing properties
 *    are left in place so every historical query keeps working.
 *  - `click_id` — dedupes the by-design multi-event clicks.
 * `affiliate_product_shown` gets `placement_key` too so the CTR denominator
 * joins on the same column.
 */
// The legacy and unified emitters name the SAME surface differently (the
// legacy `placement` vocabulary vs the unified `surface` one), so a raw
// coalesce splits one click into two placement_key rows. This map folds the
// legacy names onto the unified canon so a deduped union groups correctly.
// Guide surfaces are the one intentional gap: unified uses per-guide slugs
// (guide-<slug>) where legacy has only generic labels; those converge when
// the legacy event is retired.
const PLACEMENT_KEY_CANON: Record<string, string> = {
  'in-content': 'blog-inline-anchor',
  inline: 'blog-inline-anchor',
  callout: 'blog-callout',
  'callout-early': 'blog-callout-early',
  'card-v1': 'blog-card-v1',
  'editor-pick': 'blog-editor-pick',
  'verdict-card': 'blog-verdict-card',
  sidebar: 'guide-sidebar',
}

function withAffiliateJoinKeys(
  eventName: string,
  properties?: EventProperties,
): EventProperties | undefined {
  const isClick = AFFILIATE_CLICK_EVENTS.has(eventName)
  if (!isClick && eventName !== 'affiliate_product_shown') return properties

  const p = (properties ?? {}) as Record<string, unknown>
  const rawKey =
    (p.surface as string | undefined) ??
    (p.placement as string | undefined) ??
    (p.tracking_context as string | undefined) ??
    'unknown'
  const placementKey = PLACEMENT_KEY_CANON[rawKey] ?? rawKey

  if (!isClick) {
    return { ...properties, placement_key: placementKey } as EventProperties
  }
  return {
    ...properties,
    click_family: 'affiliate',
    placement_key: placementKey,
    click_id: affiliateClickId(),
  } as EventProperties
}

/**
 * Event parameter names GA4 reads as traffic-source or page context instead of
 * as plain event data. An event that carries `source: 'blog_modal'` starts a
 * new GA4 session attributed to source "blog_modal", medium "(not set)". The
 * app's own analytics vocabulary uses `source`, `content` and friends freely,
 * so these keys are renamed with a `wgis_` prefix on the way to GA4 only.
 */
const GA4_RESERVED_EVENT_PARAMS = new Set([
  'source',
  'medium',
  'campaign',
  'term',
  'content',
  'campaign_id',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'srsltid',
  'page_location',
  'page_referrer',
  'page_title',
  'page_path',
])

/** GA4 copy of an event's properties with traffic-source keys renamed. */
export function toGa4EventParams(props: EventProperties | undefined): EventProperties {
  const out: EventProperties = {}
  if (!props) return out
  for (const [key, value] of Object.entries(props)) {
    const reserved =
      GA4_RESERVED_EVENT_PARAMS.has(key) || key.startsWith('campaign_') || key.startsWith('utm_')
    out[reserved ? `wgis_${key}` : key] = value
  }
  return out
}

/**
 * Track a custom event to both Google Analytics 4 and PostHog
 *
 * GA4: For Google Ads integration and broader ecosystem
 * PostHog: For session replays, funnels, and UX analysis
 */
export const trackEvent = (
  eventName: string,
  properties?: EventProperties
): void => {
  if (typeof window === 'undefined') return

  const props = withAffiliateJoinKeys(eventName, properties)

  // Track to Google Analytics 4 (queues into dataLayer if gtag.js isn't loaded yet)
  const gtag = ensureGtag()
  if (gtag) {
    try {
      gtag('event', eventName, toGa4EventParams(props))
    } catch (error) {
      console.error('Failed to track event to GA4:', eventName, error)
    }
  }

  // Track to PostHog
  if (posthog) {
    try {
      if (INSTANT_SEND_EVENTS.has(eventName)) {
        posthog.capture(eventName, props || {}, { send_instantly: true })
      } else {
        posthog.capture(eventName, props || {})
      }
    } catch (error) {
      console.error('Failed to track event to PostHog:', eventName, error)
    }
  }
}

/**
 * Run a callback once the page is genuinely visible to the user, or never.
 *
 * posthog-js gates the initial `$pageview` on `document.visibilityState ===
 * 'visible'` — background-tab opens and Chrome SERP prerenders execute all of
 * our React effects while hidden, but PostHog holds the pageview until the tab
 * is foregrounded. Mount-time "viewed/shown" events fired straight from
 * useEffect had no such gate, so hidden pages emitted them into sessions that
 * never received a `$pageview` (~17k such sessions / 28d as of 2026-08-01,
 * inflating exposure counts 10-20%). Route any exposure-style event that fires
 * on mount through this helper so its counts mean "a person saw this",
 * matching PostHog's own pageview semantics.
 *
 * Returns a cleanup function (for useEffect) that cancels a still-pending
 * callback; a page that is never foregrounded fires nothing, by design.
 */
export function runWhenPageVisible(callback: () => void): () => void {
  if (typeof document === 'undefined') return () => {}
  const doc = document as Document & { prerendering?: boolean }

  if (!doc.prerendering && doc.visibilityState === 'visible') {
    callback()
    return () => {}
  }

  let cancelled = false
  const tryFire = () => {
    if (cancelled) return
    if (doc.prerendering || doc.visibilityState !== 'visible') return
    cleanup()
    callback()
  }
  const cleanup = () => {
    cancelled = true
    doc.removeEventListener('visibilitychange', tryFire)
    doc.removeEventListener('prerenderingchange', tryFire)
  }
  doc.addEventListener('visibilitychange', tryFire)
  // Prerendered documents report visibilityState 'hidden' and fire
  // `prerenderingchange` on activation; listen to both so neither path leaks.
  doc.addEventListener('prerenderingchange', tryFire)
  return cleanup
}

/**
 * Result-CTA location scope.
 *
 * `result_primary_cta_clicked` is fired from a callback that several result
 * screens share — the use-homepage-analysis handler is passed as
 * `onPrimaryCtaClick` to both the identify and the diagnose result, and the
 * saved-result page reuses one handler across its two. The callback therefore
 * cannot know which surface invoked it, which is why 100% of the clicks it
 * emitted carried no `cta_location`.
 *
 * The host render site DOES know. It wraps the shared handler here, and the
 * emitter reads the scope. This is only sound because the handler emits
 * synchronously: the scope is set, the handler runs, and `finally` clears it
 * before control returns. Do not wrap an async handler — an await would let the
 * scope leak to whatever fires next. Prefer passing `ctaLocation` outright when
 * you own the handler.
 */
/**
 * Result-CTA surface names. The union documents the values the result screens
 * send today without closing the type: new surfaces ship before this list is
 * updated, and a closed union would break them at the call site rather than in
 * the dashboard where the mismatch is actually visible.
 */
export type ResultCtaLocation =
  | 'hero_pills'
  | (string & {})

let currentResultCtaLocation: ResultCtaLocation | undefined

export function withResultCtaLocation(
  ctaLocation: ResultCtaLocation,
  handler?: (method: 'oauth' | 'email') => void,
): (method: 'oauth' | 'email') => void {
  return (method) => {
    currentResultCtaLocation = ctaLocation
    try {
      handler?.(method)
    } finally {
      currentResultCtaLocation = undefined
    }
  }
}

/**
 * Roll a granular `email_captured` source string up to a stable, readable
 * surface FAMILY. This is ADDITIVE — the raw `source` is still sent on every
 * event for per-tool / per-post / per-topic drill-down — but the family lets a
 * dashboard collapse the noise the raw strings carry:
 *   - per-tool modal labels ("Fertilizer Calculator_modal") → "modal"
 *   - per-tool result cards ("tool_result_fertilizer")       → "tool_result"
 *   - per-topic reminder widgets ("blog_reminder_widget_*")  → "blog_reminder_widget"
 *   - first-vs-returning suffix ("*_returning")              → folded into the family
 *   - products-guide A/B suffix ("products_guide_2026__cta_x")→ "products_guide"
 * Keep raw `source` for granularity; use `surface_family` for the high-level view.
 */
export function emailSurfaceFamily(source: string | undefined | null): string {
  if (!source) return 'other'
  // Fold the first-vs-returning-visitor suffix and any "__"-delimited A/B suffix.
  const base = source.replace(/_returning$/, '').split('__')[0]
  if (/_modal$/i.test(base)) return 'modal'
  if (base.startsWith('tool_result_')) return 'tool_result'
  if (base.startsWith('blog_reminder_widget_')) return 'blog_reminder_widget'
  if (base.startsWith('products_guide_2026')) return 'products_guide'
  switch (base) {
    case 'grass_id_gate': return 'grass_id_gate'
    case 'diagnosis_gate':
    case 'diagnosis_healthy':
    case 'diagnosis_failed':
    case 'diagnosis_monitor': return 'diagnosis'
    case 'guide': return 'guide_gate'
    case 'guide-read': return 'guide_read'
    case 'compare-read': return 'compare_read'
    case 'blog-post-read': return 'blog_post_read'
    case 'blog-inline': return 'blog_inline'
    case 'footer': return 'footer'
    case 'care_plan_page': return 'care_plan_page'
    case 'waitlist':
    case 'app_waitlist': return 'waitlist'
    case 'contact_form': return 'contact_form'
    case 'homepage': return 'homepage'
    case 'map_draw': return 'map_draw'
    default: return base
  }
}

/**
 * Track a conversion event with monetary value
 */
export const trackConversion = (
  conversionType: string,
  value?: number,
  currency: string = 'USD'
): void => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value || 0,
    currency
  })
}

/**
 * Track a page view (called automatically by Next.js, but can be used manually)
 */
export const trackPageView = (path: string): void => {
  const gtag = ensureGtag()
  if (!gtag) return

  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID
  if (!measurementId) {
    console.warn('GA_MEASUREMENT_ID not configured')
    return
  }

  try {
    gtag('config', measurementId, {
      page_path: path
    })
  } catch (error) {
    console.error('Failed to track page view:', error)
  }
}

// ============================================
// GATE CONTEXT (reason + surface)
// ============================================

/**
 * WHY a gate, wall, warning or upgrade prompt was shown.
 *
 * `analysis_limit_reached`, `result_upgrade_prompted`, `scan_quota_warning_shown`
 * and `analysis_resume_wall_shown` all carried null on both `reason` and
 * `surface` for 4,733 events, so every gate impression looked the same in the
 * funnel. Both are required on every emitter below.
 */
export type AnalysisGateReason =
  /** Signed-out browser with no email spent the no-account allowance. */
  | 'anon_quota_exhausted'
  /** Signed-out browser that HAS given us an email spent the email-tier allowance. */
  | 'email_tier_quota_exhausted'
  /** Signed-in free account spent its monthly allowance. */
  | 'registered_quota_exhausted'
  /** The action itself is paid, independent of any counter. */
  | 'premium_required'
  /** Allowance not spent yet — the pre-emptive ask on the way up. */
  | 'quota_low'
  /** The server capped the photo count for this tier. */
  | 'photo_cap'
  /** Result content is behind an account/email, not behind a counter. */
  | 'result_locked'
  /** A saved analysis needs an account to persist. */
  | 'save_required'
  /** The gate fired but the caller genuinely cannot attribute it. Never
   *  acceptable as a default — pass a real reason. */
  | 'unknown'

/**
 * WHERE the gate was shown. Convention: `<route surface>:<placement>`, e.g.
 * `homepage:care_plan_upgrade`, `diagnose:resume_wall`,
 * `weed_identifier:limit_wall`, `saved_result:upgrade_card`. The route half
 * matches `surfaceFromPathname`, so it joins with `route_surface` on the
 * funnel envelope.
 */
export type AnalysisGateSurface = string

function gateContext(
  reason: AnalysisGateReason,
  surface: AnalysisGateSurface,
  mode?: AnalysisFunnelMode,
  userTier?: AnalyticsUserTier | null,
): EventProperties {
  return {
    reason,
    surface: surface || currentRouteSurface(),
    route_surface: currentRouteSurface(),
    entry_surface: sessionEntrySurface(),
    // The ambient rung is published by an effect, so a gate whose own response
    // just told us the rung (the analyze route's 429 `tier`) would report the
    // pre-observation value. An explicit tier resolved at the call site wins.
    user_tier: userTier ?? ambientUserTier,
    ...(mode ? { mode } : {}),
  }
}

// ============================================
// RESULT STATE + NEXT ACTION (Wave 2 call sites)
// ============================================

/**
 * WHAT the user is actually looking at once a scan settles.
 *
 * `grass_id_result_rendered` fires for every outcome, so "identified" and
 * "we could not read your photo" are one number today. These are the exact
 * states the three result components can be in.
 */
export type ResultStateId =
  // identify
  | 'identified'
  | 'low_confidence'
  | 'non_grass_subject'
  | 'inconclusive'
  | 'poor_image'
  | 'unsupported'
  // diagnose
  | 'diagnose_problems'
  | 'diagnose_healthy'
  | 'diagnose_inconclusive'
  | 'diagnose_failed'
  // weed
  | 'weed_supported'
  | 'weed_unsupported'
  | 'weed_inconclusive'

/**
 * The action offered from a result state. Free-form so a new CTA does not need
 * a lib change, but keep the ids snake_case and stable — they are the funnel's
 * x-axis (e.g. 'retake_photo', 'add_supporting_photo', 'save_result',
 * 'switch_to_diagnose', 'view_care_plan', 'sign_in', 'upgrade', 'share').
 */
export type ResultNextActionId = string

// ============================================
// TIER 1: CRITICAL CONVERSION EVENTS
// ============================================

/**
 * Grass ID Funnel Events
 */
export const analytics = {
  // Photo uploaded
  grassIdPhotoUploaded: (
    fileSizeKb: number,
    uploadMethod: 'file' | 'file-compressed' | 'camera',
    extra?: {
      attemptId?: string
      source?: 'file' | 'drop' | 'camera'
      mode?: 'identify' | 'diagnose'
      originalSizeKb?: number
      width?: number
      height?: number
      highFidelity?: boolean
      imageRole?: 'primary' | 'supporting'
      fidelityFallbackReason?: string
      clientPreparedSizeKb?: number
      serverQuality?: number
      serverResized?: boolean
      funnelId?: string
      originLawnId?: string | null
      selectedLawnId?: string | null
      authState?: 'anonymous' | 'authenticated' | 'unknown'
      /**
       * WHERE the photo was picked, distinct from HOW (`source`). 'homepage'
       * for the normal uploader; a cta_location string (e.g.
       * 'blog_inline_identify_grass') when the file rode the blog inline-scan
       * handoff (lib/pending-scan-file.ts). Closes the attribution gap that
       * made blog-originated uploads uncountable.
       */
      entrySource?: string
    },
  ) => {
    trackEvent('grass_id_photo_uploaded', {
      ...analysisFunnelEnvelope({
        funnelId: extra?.funnelId,
        attemptId: extra?.attemptId,
        originLawnId: extra?.originLawnId,
        selectedLawnId: extra?.selectedLawnId,
        mode: extra?.mode ?? 'identify',
        source: extra?.entrySource ?? resolveFunnelSource(),
        imageRoles: extra?.imageRole ?? 'primary',
        authState: extra?.authState,
      }),
      file_size_kb: fileSizeKb,
      upload_method: uploadMethod,
      // Capture path (camera vs picker vs drag-drop); `upload_method` above
      // predates it and only carries the compression flag. Keep it separate
      // from the required funnel `source`, which describes journey origin.
      capture_source: extra?.source ?? null,
      entry_source: extra?.entrySource ?? sessionEntrySurface() ?? 'unknown',
      // Identify vs diagnose. The toggle state is known at upload time, so
      // stamp it alongside the fidelity and compression dimensions.
      original_size_kb: extra?.originalSizeKb,
      image_width: extra?.width,
      image_height: extra?.height,
      high_fidelity: extra?.highFidelity,
      image_role: extra?.imageRole ?? 'primary',
      fidelity_fallback_reason: extra?.fidelityFallbackReason,
      client_prepared_size_kb: extra?.clientPreparedSizeKb,
      server_quality: extra?.serverQuality,
      server_resized: extra?.serverResized,
    })
  },

  // Fires the first time a typed ZIP becomes valid in a given analysis flow.
  // Deduped per ZIP value at the call site so distinct re-uploads with new
  // ZIPs are still captured (the multi-upload pattern is signal, not noise).
  grassIdZipValidEntered: (props: {
    zipPrefix: string
    /** Optional. Omit to derive the real entry route (was hardcoded 'homepage'). */
    source?: string
    entryMethod?: 'typed' | 'saved' | 'geolocation' | 'url_restore' | 'ip'
    mode: 'identify' | 'diagnose'
    attemptId?: string
    funnelId?: string
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_zip_valid_entered', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        mode: props.mode,
        source: props.source,
        authState: props.authState,
      }),
      zip_prefix: props.zipPrefix,
      entry_method: props.entryMethod,
    })
  },

  // zip_ip_prefill_v1 treatment: the ZIP step filled an empty field from the
  // visitor's IP location. Once per attempt at the fill.
  zipPrefillShown: (props: {
    zipPrefix: string
    mode: 'identify' | 'diagnose'
    attemptId?: string | null
    funnelId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('zip_prefill_shown', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        mode: props.mode,
        source: 'zip_step',
        authState: props.authState,
      }),
      zip_prefix: props.zipPrefix,
      platform: 'web',
    })
  },

  // First user change to an IP-prefilled ZIP in an attempt. changed_to_valid
  // is true when that change already leaves a different five-digit ZIP.
  zipPrefillEdited: (props: {
    changedToValid: boolean
    entryMethod: 'typed' | 'saved' | 'geolocation'
    mode: 'identify' | 'diagnose'
    attemptId?: string | null
    funnelId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('zip_prefill_edited', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        mode: props.mode,
        source: 'zip_step',
        authState: props.authState,
      }),
      changed_to_valid: props.changedToValid,
      entry_method: props.entryMethod,
      platform: 'web',
    })
  },

  // Fires when the analyze API call begins (after photo + valid ZIP, after
  // dedupe of double-clicks). Pairs with grassIdAnalysisFailed/Completed.
  grassIdAnalysisStarted: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    /** Optional. Omit to derive the real entry route (was hardcoded 'homepage'). */
    source?: string
    funnelId?: string
    originLawnId?: string | null
    selectedLawnId?: string | null
    imageRoles?: readonly string[]
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_analysis_started', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        originLawnId: props.originLawnId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: props.source,
        imageRoles: props.imageRoles,
        authState: props.authState,
      }),
    })
  },

  // Analysis failed (network/server error path — distinct from limit/upgrade).
  grassIdAnalysisFailed: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    errorCategory: string
    errorMessage?: string
    httpStatus?: number
    serverErrorCode?: string
    funnelId?: string
    analysisId?: string | null
    selectedLawnId?: string | null
    source?: string
    authState?: 'anonymous' | 'authenticated' | 'unknown'
    /** True when the UI kept the photo and offered a same-photo retry. */
    retryOffered?: boolean
  }) => {
    trackEvent('grass_id_analysis_failed', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        analysisId: props.analysisId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: resolveFunnelSource(props.source),
        authState: props.authState,
      }),
      error_category: props.errorCategory,
      error_message: props.errorMessage,
      http_status: props.httpStatus,
      server_error_code: props.serverErrorCode,
      ...(props.retryOffered === undefined ? {} : { retry_offered: props.retryOffered }),
    })
  },

  // Analysis blocked because the anon/free quota was reached.
  grassIdLimitReached: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    funnelId?: string
    selectedLawnId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_limit_reached', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: resolveFunnelSource(),
        authState: props.authState,
      }),
    })
  },

  // Analysis blocked because a paid tier is required.
  grassIdUpgradeRequired: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    funnelId?: string
    selectedLawnId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_upgrade_required', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: resolveFunnelSource(),
        authState: props.authState,
      }),
    })
  },

  // Result section actually entered the viewport — answers "did the user
  // have any of this on screen?", not just "did the API succeed?". Pairs
  // with grass_id_result_rendered (scroll-independent) — the delta between
  // them reveals real bounce-before-scroll.
  //
  // `restored` marks views of a saved/restored result (/result/[id] or a
  // rehydrated session) where NO in-session API success fired, so
  // grass_id_result_rendered never emitted for it. Without this flag those
  // views inflate `viewed` above `rendered` and invert the bounce-before-
  // scroll delta — filter `restored = false` for the fresh-analysis funnel.
  grassIdResultViewed: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    grassType?: string
    confidence?: number
    restored?: boolean
    funnelId?: string
    analysisId?: string | null
    selectedLawnId?: string | null
    source?: string
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_result_viewed', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        analysisId: props.analysisId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: props.source ?? (props.restored ? 'saved_result' : resolveFunnelSource()),
        rawConfidence: props.confidence,
        displayConfidence: props.confidence,
        authState: props.authState,
        actualDestination: 'result_viewport',
      }),
      grass_type: props.grassType,
      confidence: props.confidence === undefined
        ? undefined
        : Math.round(props.confidence * 100) / 100,
      restored: props.restored,
    })
  },

  // Result state was set after a successful API response. Fires immediately
  // on `setAnalysisResult`/`setDiagnosisResult`, independent of scroll and
  // IntersectionObserver. Compared against `grass_id_result_viewed`, the
  // delta isolates "users who bounced before scrolling" from
  // "instrumentation lost the event" (hydration crash, observer torn down,
  // ad-blocker stripped posthog, etc.).
  grassIdResultRendered: (props: {
    attemptId: string
    mode: 'identify' | 'diagnose'
    grassType?: string
    confidence?: number
    analysisId?: string
    model?: string
    pipelineVersion?: string
    funnelId?: string
    originLawnId?: string | null
    selectedLawnId?: string | null
    zipMatch?: boolean | null
    source?: string
    imageRoles?: readonly string[]
    displayConfidence?: number | null
    gatingReasons?: readonly string[] | string | null
    safetyProof?: string | null
    promptVersion?: string | null
    promptStrategy?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
    actualDestination?: string | null
    processingTimeMs?: number
    userType?: 'anonymous' | 'authenticated'
    imageQuality?: string
    isUnknownOther?: boolean
    abstentionReason?: string
    candidateMargin?: number
    imageCount?: number
    rescanOfAnalysisId?: string
    hostGrassVerified?: boolean
    imageWidth?: number
    imageHeight?: number
    originalSizeKb?: number
    uploadedSizeKb?: number
  }) => {
    trackEvent('grass_id_result_rendered', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        analysisId: props.analysisId,
        originLawnId: props.originLawnId,
        selectedLawnId: props.selectedLawnId,
        zipMatch: props.zipMatch,
        mode: props.mode,
        source: resolveFunnelSource(props.source),
        imageRoles: props.imageRoles,
        rawConfidence: props.confidence,
        displayConfidence: props.displayConfidence ?? props.confidence,
        gatingReasons: props.gatingReasons,
        safetyProof: props.safetyProof,
        model: props.model,
        promptVersion: props.promptVersion,
        promptStrategy: props.promptStrategy,
        pipelineVersion: props.pipelineVersion,
        authState: props.authState ?? props.userType,
        actualDestination: props.actualDestination ?? 'result_screen',
      }),
      grass_type: props.grassType,
      confidence: props.confidence === undefined
        ? undefined
        : Math.round(props.confidence * 100) / 100,
      processing_time_ms: props.processingTimeMs,
      user_type: props.userType,
      image_quality: props.imageQuality,
      is_unknown_other: props.isUnknownOther,
      abstention_reason: props.abstentionReason,
      candidate_margin: props.candidateMargin,
      image_count: props.imageCount,
      rescan_of_analysis_id: props.rescanOfAnalysisId,
      host_grass_verified: props.hostGrassVerified,
      image_width: props.imageWidth,
      image_height: props.imageHeight,
      original_size_kb: props.originalSizeKb,
      uploaded_size_kb: props.uploadedSizeKb,
    })
  },

  // User started a new attempt. `fromResult: true` means they had a result
  // visible (re-uploading after seeing one); false = abandoning earlier in flow.
  grassIdRetryStarted: (props: {
    previousAttemptId: string | null
    fromResult: boolean
    mode: 'identify' | 'diagnose'
    /** True when the client re-ran itself (post-OAuth resume auto-retry) rather
     *  than the user tapping the recovery banner. */
    auto?: boolean
    funnelId?: string
    selectedLawnId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
  }) => {
    trackEvent('grass_id_retry_started', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.previousAttemptId,
        selectedLawnId: props.selectedLawnId,
        mode: props.mode,
        source: 'homepage_retry',
        authState: props.authState,
        actualDestination: 'photo_capture',
      }),
      previous_attempt_id: props.previousAttemptId ?? undefined,
      from_result: props.fromResult,
      auto: props.auto === true,
    })
  },

  // Email captured (for anonymous users)
  emailCaptured: (
    source: string,
    grassType?: string,
    extra?: { attemptId?: string; captureType?: 'newsletter' | 'oauth_signup'; analysisId?: string; funnelId?: string },
  ) => {
    trackEvent('email_captured', {
      source,
      surface_family: emailSurfaceFamily(source),
      grass_type: grassType,
      attempt_id: extra?.attemptId,
      capture_type: extra?.captureType,
      ...(extra?.analysisId ? { analysis_id: extra.analysisId } : {}),
      ...(extra?.funnelId ? { funnel_id: extra.funnelId } : {}),
    })
  },

  // User opened the deferred soft-capture email row (e.g. the "Just email me
  // the plan" link on the home result card). Lets us measure reveal-rate
  // independently of submit-rate so we can tell whether copy or friction is
  // the bottleneck.
  // `source` is the calling surface's own source string (diagnosis_gate,
  // weed_identifier_result, …), matching result_email_capture_focused and
  // email_captured on the same surface. It was a two-value union back when the
  // only caller hardcoded its label.
  softEmailCaptureOpened: (
    source: string,
    extra?: { attemptId?: string; grassType?: string },
  ) => {
    trackEvent('soft_email_capture_opened', {
      source,
      attempt_id: extra?.attemptId,
      grass_type: extra?.grassType,
    })
  },

  // Hybrid soft-entry layout — fires when the unblurred current-month plan
  // preview card scrolls into view. Pairs with grass_id_result_rendered to
  // measure how many users actually engage with the plan content vs bounce.
  resultPlanPreviewScrolled: (props: {
    attemptId: string
    grassType?: string
  }) => {
    trackEvent('result_plan_preview_scrolled', {
      attempt_id: props.attemptId,
      grass_type: props.grassType,
    })
  },

  // Hybrid soft-entry layout — fires when the upgrade CTA card (email/OAuth
  // capture) scrolls into view. Ratio of this event to result_rendered tells
  // us whether the upgrade is being seen at all in the soft-entry layout.
  //
  // `reason` and `surface` are REQUIRED: 4,733 historical rows carry null on
  // both, which makes every gate impression indistinguishable from every other.
  resultUpgradePrompted: (props: {
    attemptId: string
    grassType?: string
    reason: AnalysisGateReason
    surface: AnalysisGateSurface
    mode?: AnalysisFunnelMode
    /** Layout arm that rendered the ask, e.g. 'hero_pills_v1'. Keeps a bundle
     *  read attributable when two layouts ship over the same event. */
    captureLayout?: string
  }) => {
    trackEvent('result_upgrade_prompted', {
      attempt_id: props.attemptId,
      grass_type: props.grassType,
      capture_layout: props.captureLayout,
      ...gateContext(props.reason, props.surface, props.mode),
    })
  },

  // Fires when the user takes the upgrade card's conversion action — an OAuth
  // button click or the email-plan submit. Splits the two paths via `method`,
  // giving the click step between result_upgrade_prompted (seen) and
  // email_captured / signup_completed (converted).
  //
  // `ctaLocation` names the result surface that fired it. Pass it explicitly
  // where the caller knows the surface; where the handler is shared across
  // several render sites (the use-homepage-analysis callback serves both the
  // identify and diagnose results), the host wraps it in withResultCtaLocation
  // instead and this falls back to that scope.
  resultPrimaryCtaClicked: (props: {
    /** Result surface that fired this, e.g. 'home_identify_result'. */
    ctaLocation?: ResultCtaLocation
    attemptId: string
    grassType?: string
    /** Diagnosed problem name — the diagnosis-flow equivalent of grassType.
     *  Without it, diagnosis CTA clicks read with no subject dimension. */
    problem?: string
    method: 'oauth' | 'email'
    confidence?: number
    entryPath?: 'homepage' | 'saved_result' | 'diagnose'
    analysisId?: string
    mode?: 'identify' | 'diagnose'
    planType?: 'care' | 'treatment' | 'monitoring'
    outcome?: string
    /** Layout arm that rendered the CTA, e.g. 'hero_pills_v1'. Same key as
     *  result_upgrade_prompted, so seen→clicked joins per layout. */
    captureLayout?: string
  }) => {
    trackEvent('result_primary_cta_clicked', {
      cta_location: props.ctaLocation ?? currentResultCtaLocation,
      capture_layout: props.captureLayout,
      attempt_id: props.attemptId,
      grass_type: props.grassType,
      problem: props.problem,
      method: props.method,
      confidence: props.confidence,
      entry_path: props.entryPath,
      analysis_id: props.analysisId,
      mode: props.mode,
      plan_type: props.planType,
      outcome: props.outcome,
    })
  },

  // Quota-aware early ask — fires when the anonymous scan unlock banner
  // renders on an anon result (ask on the way up, not at the lockout wall).
  //
  // `reason` and `surface` are required for the same reason they are on
  // result_upgrade_prompted — see the note there.
  scanQuotaWarningShown: (props: {
    attemptId: string
    remaining: number
    grassType?: string
    reason: AnalysisGateReason
    surface: AnalysisGateSurface
    mode?: AnalysisFunnelMode
  }) => {
    trackEvent('scan_quota_warning_shown', {
      attempt_id: props.attemptId,
      remaining: props.remaining,
      grass_type: props.grassType,
      ...gateContext(props.reason, props.surface, props.mode),
    })
  },

  scanQuotaWarningClicked: (props: {
    attemptId: string
    remaining: number
    grassType?: string
  }) => {
    trackEvent('scan_quota_warning_clicked', {
      attempt_id: props.attemptId,
      remaining: props.remaining,
      grass_type: props.grassType,
    })
  },

  /**
   * The analyze request was walled by a quota. Existing properties are
   * unchanged; `reason` and `surface` are new and required.
   */
  analysisLimitReached: (props: {
    mode: AnalysisFunnelMode
    reason: AnalysisGateReason
    surface: AnalysisGateSurface
    analysisResumeId?: string | null
    entryPath?: string | null
    imageCount?: number
    attemptId?: string | null
    funnelId?: string | null
    /** The rung this very response proved, when the caller learned it here. */
    userTier?: AnalyticsUserTier | null
  }) => {
    trackEvent('analysis_limit_reached', {
      analysis_resume_id: props.analysisResumeId ?? undefined,
      entry_path: props.entryPath ?? undefined,
      image_count: props.imageCount,
      attempt_id: props.attemptId ?? undefined,
      funnel_id: props.funnelId ?? undefined,
      ...gateContext(props.reason, props.surface, props.mode, props.userTier),
    })
  },

  /**
   * The post-OAuth resume wall rendered. Callers pass their existing context
   * object through `extra`; `reason` and `surface` are new and required.
   */
  analysisResumeWallShown: (
    props: {
      mode: AnalysisFunnelMode
      reason: AnalysisGateReason
      surface: AnalysisGateSurface
    },
    extra?: EventProperties,
  ) => {
    trackEvent('analysis_resume_wall_shown', {
      ...extra,
      ...gateContext(props.reason, props.surface, props.mode),
    })
  },

  /**
   * TRUE step impression for the ZIP step: the denominator for the photo -> ZIP
   * drop. Fire it through `useVisibleImpression` (or `runWhenPageVisible`) so it
   * keeps the same visibility semantics as every other impression event, and
   * dedupe on `${attemptId}:${mode}`.
   */
  /**
   * WHAT the OAuth return actually landed on for a refinement resume.
   * `analysis_url_restored` is identical for a visitor who lands in the
   * refinement step and one who lands on a bare ZIP form, which is the exact
   * loss the photo-cap gate funnel keeps reporting with no way to see it.
   * `has_lineage` separates a free re-run from a billed one.
   */
  refinementResumeLanded: (props: {
    mode: AnalysisFunnelMode
    hasLineage: boolean
    hasPrevOutcome: boolean
    enteredRefinementStep: boolean
    trigger?: string | null
  }) => {
    trackEvent('refinement_resume_landed', {
      mode: props.mode,
      has_lineage: props.hasLineage,
      has_prev_outcome: props.hasPrevOutcome,
      entered_refinement_step: props.enteredRefinementStep,
      trigger: props.trigger ?? null,
      platform: 'web',
    })
  },

  zipStepShown: (props: {
    mode: AnalysisFunnelMode
    attemptId?: string | null
    funnelId?: string | null
    authState?: 'anonymous' | 'authenticated' | 'unknown'
    /** True when the step is rendering the post-result refinement variant. */
    isRefinementStep?: boolean
    photoCap?: number
  }) => {
    trackEvent('zip_step_shown', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        mode: props.mode,
        source: 'zip_step',
        authState: props.authState,
        actualDestination: 'zip_step',
      }),
      is_refinement_step: props.isRefinementStep === true,
      photo_cap: props.photoCap ?? null,
      platform: 'web',
    })
  },

  /**
   * WHICH result state the user landed on, once per settled result.
   *
   * Wave 2 call sites: components/home/results-display.tsx,
   * components/home/diagnosis-results.tsx,
   * components/weed-identifier/weed-result-presentation.tsx.
   *
   * Fire it through `useVisibleImpression` / `runWhenPageVisible`, deduped on
   * `${attemptId}:${state}`, so it matches the other impression events. Pair it
   * with `resultNextActionClicked` — the two share `state` / `from_state`, which
   * is what makes "what did we offer, from which state, and did they take it"
   * a single funnel.
   */
  resultStateShown: (props: {
    state: ResultStateId
    mode: AnalysisFunnelMode
    attemptId: string
    funnelId?: string | null
    analysisId?: string | null
    /** Action ids rendered alongside the state, in render order. */
    offeredActions?: readonly ResultNextActionId[]
    confidence?: number | null
    displayConfidence?: number | null
    grassType?: string | null
    problem?: string | null
    restored?: boolean
    authState?: 'anonymous' | 'authenticated' | 'unknown'
    source?: string
  }) => {
    trackEvent('result_state_shown', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        analysisId: props.analysisId,
        mode: props.mode,
        source: props.source,
        rawConfidence: props.confidence,
        displayConfidence: props.displayConfidence ?? props.confidence,
        authState: props.authState,
        actualDestination: 'result_state',
      }),
      state: props.state,
      offered_actions: props.offeredActions?.join(',') ?? null,
      offered_action_count: props.offeredActions?.length ?? null,
      grass_type: props.grassType ?? null,
      problem: props.problem ?? null,
      restored: props.restored === true,
      platform: 'web',
    })
  },

  /**
   * The user took one of the actions a result state offered.
   *
   * `fromState` MUST be the same value the matching `resultStateShown` carried,
   * otherwise the offered -> taken ratio cannot be computed per state.
   */
  resultNextActionClicked: (props: {
    action: ResultNextActionId
    fromState: ResultStateId
    mode: AnalysisFunnelMode
    attemptId: string
    funnelId?: string | null
    analysisId?: string | null
    /** Zero-based position of the action among the offered set. */
    position?: number
    authState?: 'anonymous' | 'authenticated' | 'unknown'
    source?: string
  }) => {
    trackEvent('result_next_action_clicked', {
      ...analysisFunnelEnvelope({
        funnelId: props.funnelId,
        attemptId: props.attemptId,
        analysisId: props.analysisId,
        mode: props.mode,
        source: props.source,
        authState: props.authState,
        actualDestination: `next_action:${props.action}`,
      }),
      action: props.action,
      from_state: props.fromState,
      position: props.position ?? null,
      platform: 'web',
    })
  },

  // Hybrid soft-entry layout — fires when the product picks section enters
  // the viewport. Combined with result_plan_preview_scrolled and
  // result_upgrade_prompted, gives a complete section-by-section scroll
  // funnel so we can measure whether users actually engage with the
  // monetization surface or scroll past it.
  resultProductsScrolled: (props: {
    attemptId: string
    grassType?: string
  }) => {
    trackEvent('result_products_scrolled', {
      attempt_id: props.attemptId,
      grass_type: props.grassType,
    })
  },

  // Hybrid soft-entry layout — fires when the tool deeplinks section enters
  // the viewport. The drop between this event and result_products_scrolled
  // tells us whether users skip the affiliate band to keep reading, vs the
  // drop between this and result_upgrade_prompted which tells us whether
  // tool clicks are an exit path to non-conversion engagement.
  resultToolsScrolled: (props: {
    attemptId: string
    grassType?: string
  }) => {
    trackEvent('result_tools_scrolled', {
      attempt_id: props.attemptId,
      grass_type: props.grassType,
    })
  },

  // Premium Conversion Funnel
  upgradePromptShown: (trigger: 'usage_limit' | 'feature_gate' | 'pricing_page', currentUsage: number, limit: number) => {
    trackEvent('upgrade_prompt_shown', {
      trigger,
      current_usage: currentUsage,
      limit
    })
  },

  upgradeClicked: (
    source:
      | 'usage_limit_modal'
      | 'pricing_page'
      | 'dashboard'
      | 'homepage_pricing_preview'
      | 'upgrade_prompt_banner'
      | 'upgrade_prompt_card'
      | 'upgrade_prompt_inline'
      | 'save_lawn_limit_modal',
    selectedTier?: 'monthly' | 'yearly',
  ) => {
    trackEvent('upgrade_clicked', {
      source,
      selected_tier: selectedTier,
    })
  },

  checkoutInitiated: (tier: 'monthly' | 'yearly', amount: number) => {
    trackEvent('checkout_initiated', {
      tier,
      amount
    })
  },

  /**
   * The buyer reached the post-checkout success screen. BEHAVIORAL ONLY: web
   * purchase revenue is booked exactly once, by the Stripe webhook
   * (`premium_activated` / `subscription_renewed` in lib/server-analytics.ts).
   * This event therefore carries no `$revenue` and fires no conversion.
   */
  checkoutSuccessViewed: (tier: 'monthly' | 'yearly', amount: number) => {
    trackEvent('checkout_success_viewed', {
      tier,
      amount
    })
  },

  /**
   * @deprecated No live call sites. The client fire double-booked every web
   * purchase against the Stripe webhook, which is now the single source of web
   * revenue truth; the checkout success screen emits `checkoutSuccessViewed`
   * instead. Kept exported because `premium_activated` has event history and
   * docs reference this helper — do not re-wire it to a client surface.
   */
  premiumActivated: (tier: 'monthly' | 'yearly', amount: number, timeToConvertHours?: number) => {
    trackEvent('premium_activated', {
      tier,
      amount,
      time_to_convert_hours: timeToConvertHours
    })

    // Also track as a conversion
    trackConversion('premium_subscription', amount)
  },

  // Calculator Engagement
  //
  // COUNTING CONVENTION: `calculator_used` fires for both intentional runs
  // (`source: 'form_submit' | 'url_params' | 'quick_start' | 'saved_profile'`)
  // AND every live-slider recompute (`source: 'live_update'`). Live updates are
  // ~half of raw volume, so any "calculations" / conversion metric MUST filter
  // to the intentional sources (exclude `live_update` / `mobile_live_update`) or
  // it over-counts ~2x. Raw event count is a usage/engagement signal, not a
  // conversion signal.
  calculatorUsed: (
    calculatorType: CalculatorType,
    payloadOrGrassType?: CalculatorPayload | string,
    lawnSizeSqft?: number,
    userTier?: 'free' | 'premium',
    location?: string
  ) => {
    if (payloadOrGrassType && typeof payloadOrGrassType === 'object') {
      const { inputs, result, userTier: tier, location: loc, source } = payloadOrGrassType
      const flatInputs: EventProperties = {}
      for (const [k, v] of Object.entries(inputs)) {
        flatInputs[k] = Array.isArray(v) ? v.join(',') : v
      }
      const flatResult: EventProperties = {}
      if (result) {
        for (const [k, v] of Object.entries(result)) {
          flatResult[`result_${k}`] = v
        }
      }
      trackEvent('calculator_used', {
        ...calculatorTypeProps(calculatorType),
        ...flatInputs,
        ...flatResult,
        user_tier: tier,
        location: loc,
        source
      })
      return
    }
    trackEvent('calculator_used', {
      ...calculatorTypeProps(calculatorType),
      grass_type: payloadOrGrassType,
      lawn_size_sqft: lawnSizeSqft,
      user_tier: userTier,
      location: location
    })
  },

  // Fired once per map session the first time the user enters drawing mode.
  // Pairs with `calculator_used` — which only fires when a polygon is actually
  // closed and the net area changes — to form a started -> completed funnel.
  // A session with `lawn_draw_started` but no `calculator_used` is a user who
  // began measuring but never produced a polygon: the previously-invisible
  // "no result" failure surfaced in the 2026-06 replay analysis.
  lawnDrawStarted: () => {
    trackEvent('lawn_draw_started', {
      ...calculatorTypeProps('lawn-size-calculator'),
      source: 'map_draw',
    })
  },

  // Fires once, on the first meaningful form input, for GATED calculators
  // (ZIP/region-gated or explicit-submit) where `calculator_used` cannot fire
  // until a downstream gate resolves (valid ZIP, submitted lab report). Pairs
  // with `calculator_used` to form a started -> completed funnel, so a user who
  // starts but never clears the gate — previously invisible — becomes a
  // measurable drop. NOT emitted by live-useMemo tools: those fire
  // `calculator_used` (source: 'live_update') on the first keystroke already,
  // so `started` would just duplicate `used` there. Sibling of lawnDrawStarted
  // / soilTempSearchStarted, which own the map + soil-temp start signals.
  calculatorStarted: (calculatorType: CalculatorType) => {
    trackEvent('calculator_started', calculatorTypeProps(calculatorType))
  },

  // Soil Temperature specific events
  soilTempLayerToggled: (
    layer: 'current' | '5yr' | '10yr',
    enabled: boolean,
    activeLayers: string[]
  ) => {
    trackEvent('soil_temp_layer_toggled', {
      layer,
      enabled,
      active_layers: activeLayers.join(',')
    })
  },

  /** Pricing billing-interval toggle — the highest-intent micro-conversion on
   * the pricing page (proves whether annual framing holds). upgrade_clicked /
   * checkout_initiated only carry the FINAL interval; this captures the switch. */
  billingIntervalToggled: (interval: 'monthly' | 'yearly') => {
    trackEvent('billing_interval_toggled', { interval })
  },

  soilTempSearchStarted: (props: {
    inputType: 'zip' | 'city'
    source: 'form_submit' | 'url_params' | 'quick_start'
    dateRangeDays: number
    activeLayers: string[]
    grassType: string
    zipPrefix?: string
  }) => {
    trackEvent('soil_temp_search_started', {
      input_type: props.inputType,
      source: props.source,
      date_range_days: props.dateRangeDays,
      active_layers: props.activeLayers.join(','),
      grass_type: props.grassType,
      zip_prefix: props.zipPrefix,
    })
  },

  soilTempCurrentLoaded: (props: {
    inputType: 'zip' | 'city'
    source: 'form_submit' | 'url_params' | 'quick_start'
    state?: string
    latBand?: string
    lonBand?: string
    grassType: string
    latestTempF?: number
    trendFPerDay?: number
    dataPoints?: number
    dateRangeDays: number
    activeLayers: string[]
    durationMs: number
  }) => {
    trackEvent('soil_temp_current_loaded', {
      input_type: props.inputType,
      source: props.source,
      state: props.state,
      lat_band: props.latBand,
      lon_band: props.lonBand,
      grass_type: props.grassType,
      latest_temp_f: props.latestTempF,
      trend_f_per_day: props.trendFPerDay,
      data_points: props.dataPoints,
      date_range_days: props.dateRangeDays,
      active_layers: props.activeLayers.join(','),
      duration_ms: props.durationMs,
    })
  },

  soilTempSearchFailed: (props: {
    inputType: 'zip' | 'city' | 'empty'
    source: 'form_submit' | 'url_params' | 'quick_start'
    stage: 'validation' | 'current' | 'historical'
    errorCategory: string
    durationMs?: number
  }) => {
    trackEvent('soil_temp_search_failed', {
      input_type: props.inputType,
      source: props.source,
      stage: props.stage,
      error_category: props.errorCategory,
      duration_ms: props.durationMs,
    })
  },

  soilTempHistoricalLoaded: (props: {
    years: 5 | 10
    status: 'success' | 'error'
    durationMs: number
    activeLayers: string[]
    errorCategory?: string
  }) => {
    trackEvent('soil_temp_historical_loaded', {
      years: props.years,
      status: props.status,
      duration_ms: props.durationMs,
      active_layers: props.activeLayers.join(','),
      error_category: props.errorCategory,
    })
  },

  soilTempDateRangeChanged: (props: {
    dateRangeDays: number
    activeLayers: string[]
    hasResults: boolean
  }) => {
    trackEvent('soil_temp_date_range_changed', {
      date_range_days: props.dateRangeDays,
      active_layers: props.activeLayers.join(','),
      has_results: props.hasResults,
    })
  },

  soilTempGrassSeasonChanged: (props: {
    grassType: 'cool' | 'warm'
    currentTempF?: number
    hasResults: boolean
  }) => {
    trackEvent('soil_temp_grass_season_changed', {
      grass_type: props.grassType,
      current_temp_f: props.currentTempF,
      has_results: props.hasResults,
    })
  },

  soilTempSectionToggled: (props: {
    section: string
    expanded: boolean
    hasResults: boolean
  }) => {
    trackEvent('soil_temp_section_toggled', {
      section: props.section,
      expanded: props.expanded,
      has_results: props.hasResults,
    })
  },

  calculatorResultSaved: (calculatorType: string, userTier: 'free' | 'premium') => {
    trackEvent('calculator_result_saved', {
      calculator_type: calculatorType,
      user_tier: userTier
    })
  },

  calculatorShared: (calculatorType: string, shareMethod: 'link' | 'social') => {
    trackEvent('calculator_shared', {
      calculator_type: calculatorType,
      share_method: shareMethod
    })
  },

  // Newsletter Engagement

  /**
   * One uniform impression event for every email-capture surface. The `source`
   * MUST be the same string the surface submits as its signup `source` (and the
   * subscribe API `context`) so the PostHog funnel `<surface>_shown` →
   * `newsletter_signup` joins on `source` / `surface_family`. Extra dimensions
   * (topic, slug, variant) ride along as `props`, never as a different `source`.
   */
  surfaceShown: (source: string, props?: EventProperties) => {
    trackEvent('email_capture_surface_shown', {
      source,
      surface_family: emailSurfaceFamily(source),
      ...props,
      // Coerce '' → null at the emitter: a caller passing an empty-string
      // topic polluted 2,519 rows with a third value that is neither "named"
      // nor "generic" (2026-08-28 tracking audit hygiene).
      ...(props && 'topic' in props ? { topic: props.topic || null } : {}),
    })
  },

  /**
   * Impression counterpart to `cta_clicked`, for non-email CTA surfaces (the
   * blog inline scan card, the contextual tool banner). Deliberately a SEPARATE
   * event from `email_capture_surface_shown`: mixing CTA impressions into that
   * event would inflate the email-capture denominator and break every existing
   * `surface_family` funnel.
   *
   * Contract: `ctaLocation` MUST be the same string the surface sends as
   * `cta_clicked.cta_location`, so the funnel joins on `cta_location` and CTR
   * becomes computable. Until this existed, the blog CTAs had clicks with no
   * denominator — the 2026-07 funnel audit could not tell a low-CTR placement
   * from a rarely-rendered one.
   */
  ctaSurfaceShown: (ctaLocation: string, props?: EventProperties) => {
    trackEvent('cta_surface_shown', {
      cta_location: ctaLocation,
      ...props,
    })
  },

  /**
   * `ctaSurfaceShown` behind the hidden-page guard. Impressions are the
   * denominator of every CTR, so a background-tab or SERP-prerender open that
   * counts an impression but never a click drags the rate down for a placement
   * nobody actually saw. `runWhenPageVisible` holds the event until the tab is
   * foregrounded and drops it entirely if it never is, matching posthog-js's
   * own `$pageview` semantics.
   *
   * Separate from `ctaSurfaceShown` rather than folded into it: the blog and
   * tool surfaces have emitted the unguarded event since 2026-07-22, and
   * changing their semantics mid-flight would put a step change in the middle
   * of their series. New surfaces should use this one.
   */
  ctaSurfaceShownWhenVisible: (ctaLocation: string, props?: EventProperties) => {
    runWhenPageVisible(() => {
      trackEvent('cta_surface_shown', {
        cta_location: ctaLocation,
        ...props,
      })
    })
  },

  /**
   * First interaction with an email-capture form, fired once per surface per
   * pageview on the first focus of its email (or ZIP) input. Sits between
   * `email_capture_surface_shown` and `email_captured`, which had no
   * intermediate step: 21,714 blog impressions to 263 captures could not be
   * split into "nobody started" and "starters dropped out".
   *
   * Contract: `source` MUST be the same string the surface sends on
   * `email_capture_surface_shown` (and submits as its signup source), so all
   * three events join on `source` / `surface_family`.
   */
  emailCaptureStarted: (props: {
    source: string
    topic?: string | null
    slug?: string | null
    placement?: string | null
    [key: string]: string | number | boolean | null | undefined
  }) => {
    const { source, ...rest } = props
    trackEvent('email_capture_started', {
      source,
      surface_family: emailSurfaceFamily(source),
      ...rest,
      ...('topic' in rest ? { topic: rest.topic || null } : {}),
    })
  },

  // `extra` carries `trigger_signal` (which passive signal opened it; null for
  // a manual open) and `offer` (the pitch key) so impressions split by both.
  newsletterModalShown: (toolName: string, usageCount: number, extra?: EventProperties) => {
    // `source` matches the modal's signup source (`${toolName}_modal`) so the
    // impression joins to the conversion; surface_family collapses per-tool noise.
    const source = `${toolName}_modal`
    trackEvent('newsletter_modal_shown', {
      tool_name: toolName,
      usage_count: usageCount,
      trigger_type: usageCount === 1 ? 'first_use' : 'returning_use',
      source,
      surface_family: emailSurfaceFamily(source),
      ...extra,
    })
  },

  // `topic` is the resolved pitch topic the surface showed (same value the
  // impression events carry). Additive 2026-08-27: without it on the CAPTURE
  // side, per-pitch conversion for the modal was unjoinable — impressions
  // could split by topic (email_capture_surface_shown) but captures could not,
  // which blocked the named-vs-generic lead magnet read.
  // `extra` carries join keys (analysis_id / funnel_id, trigger_signal) onto both events.
  newsletterSignup: (_email: string, source: string, topic?: string | null, offer?: string | null, extra?: EventProperties) => {
    markNewsletterSubscribed()

    trackEvent('newsletter_signup', {
      source,
      topic: topic ?? null,
      offer: offer ?? null,
      ...extra,
    })

    // Also track as email captured
    trackEvent('email_captured', {
      source: source,
      surface_family: emailSurfaceFamily(source),
      capture_type: 'newsletter',
      topic: topic ?? null,
      offer: offer ?? null,
      ...extra,
    })
  },

  modalDismissed: (modalType: string, context?: string, extra?: EventProperties) => {
    trackEvent('modal_dismissed', {
      modal_type: modalType,
      context: context,
      ...extra,
    })
  },

  // iOS smart-banner click. Apple's smart banner is rendered by iOS Safari and
  // we can't observe impressions reliably from JS. Fire this on next visit
  // when ?from=smart_banner (or document.referrer is apps.apple.com) signals
  // the user returned from an App Store hop.
  appBannerClick: (
    page: string,
    extra?: {
      source?: string
      referrerDomain?: string
    },
  ) => {
    trackEvent('app_banner_click', {
      page,
      source: extra?.source,
      referrer_domain: extra?.referrerDomain,
    })
  },

  appLandingViewed: (props: {
    page: string
    source?: string
    referrerDomain?: string
    hasAppStoreUrl: boolean
  }) => {
    trackEvent('app_landing_viewed', {
      page: props.page,
      source: props.source,
      referrer_domain: props.referrerDomain,
      has_app_store_url: props.hasAppStoreUrl,
    })
  },

  appStoreCtaClicked: (props: {
    page: string
    placement: string
    destinationUrl: string
    appId?: string
    source?: string
    referrerDomain?: string
  }) => {
    trackEvent('app_store_cta_clicked', {
      page: props.page,
      placement: props.placement,
      destination_url: props.destinationUrl,
      app_id: props.appId,
      source: props.source,
      referrer_domain: props.referrerDomain,
    })
  },
}

// ============================================
// TIER 2: USER BEHAVIOR INSIGHTS
// ============================================

/**
 * Where an affiliate link was clicked. Segments affiliate_link_clicked in PostHog.
 * The `compare-*` values are the three distinct surfaces on /compare/[slug]:
 * `compare-versus` (per-side chip on the top VS panel), `compare-verdict`
 * (Buy chip inside the Pick A/Pick B decision block), and `compare-picks`
 * (the dual product-card panel). Keeping them separate lets us compare
 * click-through per surface.
 */
export type AffiliatePlacement =
  | 'inline'
  | 'in-content'
  | 'footer'
  | 'callout'
  | 'callout-early'
  | 'sidebar'
  | 'comparison-table'
  | 'card-v1'
  | 'tool-urgent'
  | 'tool-section'
  | 'result-page-picks'
  | 'editor-pick'
  | 'verdict-card'
  | 'quick-picks-top'
  | 'table-buy-button'
  | 'recap-button'
  | 'homepage-shop-picks'
  | 'homepage-featured-guides'
  | 'products-catalog'
  | 'compare-verdict'
  | 'compare-picks'
  | 'compare-versus'
  | 'guide-early'
  | 'guide-care-task'
  | 'picks-card'
  | 'guide-section'
  | 'blog-rail-mobile'
  | 'tool-soil-temperature-germination'
  | 'diagnosis-treatment-link'
  // snake_case on purpose: the weed-identifier shelf has emitted this exact
  // string since launch. Renaming it would orphan the PostHog history.
  | 'weed_identifier_result'

/* ------------------------------------------------------------------------- *
 * Content CTA parity helpers (WP-E / P1-7, 2026-09-13 CTA tracking audit).
 * Kept in one block so the other content-tracking work packages can land
 * beside it without touching these lines.
 * ------------------------------------------------------------------------- */

/**
 * Route family for content pages. Stamped on both sides of a CTA pair so a
 * surface that renders on several route types (the picks rail, the app card)
 * can be read per family instead of per URL.
 */
export type PageFamily =
  | 'blog_post'
  | 'blog_listing'
  | 'blog_category'
  | 'blog_author'
  | 'guide_post'
  | 'guides_landing'
  | 'other'

export function pageFamilyFromPath(pathname?: string | null): PageFamily {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location?.pathname : undefined)
  if (!path) return 'other'
  if (path === '/guides' || path === '/guides/') return 'guides_landing'
  if (path.startsWith('/guides/')) return 'guide_post'
  if (path === '/blog' || path === '/blog/') return 'blog_listing'
  if (path.startsWith('/blog/category/')) return 'blog_category'
  if (path.startsWith('/blog/author/')) return 'blog_author'
  if (path.startsWith('/blog/')) return 'blog_post'
  return 'other'
}

/**
 * One property bag for BOTH sides of a CTA pair (`cta_surface_shown` and
 * `cta_clicked`). Before this, several surfaces sent `surface` or `destination`
 * on the impression only and `slug` on one side only, so a click could not be
 * attributed back to the impression's segment (2026-09-13 audit, P1-7).
 *
 * Keys with no value are omitted rather than sent as null, so a property is
 * either on both events or on neither.
 */
export function ctaSurfaceProps(
  ctaLocation: string,
  extras?: {
    surface?: string
    destination?: string | null
    slug?: string | null
    pageFamily?: PageFamily | null
    experimentVariant?: string | null
    [key: string]: string | number | boolean | null | undefined
  },
): EventProperties {
  const { surface, destination, slug, pageFamily, experimentVariant, ...rest } = extras ?? {}
  return {
    cta_location: ctaLocation,
    ...(surface ? { surface } : {}),
    ...(destination ? { destination } : {}),
    ...(slug ? { slug } : {}),
    page_family: pageFamily ?? pageFamilyFromPath(),
    ...(experimentVariant ? { experiment_variant: experimentVariant } : {}),
    ...rest,
  }
}

export const contentAnalytics = {
  // Blog article viewed
  blogArticleViewed: (articleSlug: string, category: string, readingTimeEstimate: number) => {
    trackEvent('blog_article_viewed', {
      article_slug: articleSlug,
      category,
      reading_time_estimate: readingTimeEstimate
    })
  },

  // Blog article read (measured by time on page)
  blogArticleRead: (articleSlug: string, timeOnPageSeconds: number, scrollDepthPercent: number) => {
    trackEvent('blog_article_read', {
      article_slug: articleSlug,
      time_on_page_seconds: timeOnPageSeconds,
      scroll_depth_percent: scrollDepthPercent
    })
  },

  // Internal link clicked
  internalLinkClicked: (fromArticle: string, toArticle: string, linkText: string) => {
    trackEvent('internal_link_clicked', {
      from_article: fromArticle,
      to_article: toArticle,
      link_text: linkText
    })
  },

  // Product recommendation clicked
  //
  // `props` is additive: the picks rail passes `surface` so this legacy event
  // can be grouped with the unified affiliate family, and `placement_key` is
  // derived from it with the same canon `withAffiliateJoinKeys` uses, so the
  // row joins to `affiliate_product_shown` on one column.
  productRecommendationClicked: (
    articleSlug: string,
    productName: string,
    affiliateLink: string,
    props?: EventProperties,
  ) => {
    const surface = typeof props?.surface === 'string' ? props.surface : undefined
    trackEvent('product_recommendation_clicked', {
      article_slug: articleSlug,
      product_name: productName,
      affiliate_link: affiliateLink,
      ...(surface ? { placement_key: PLACEMENT_KEY_CANON[surface] ?? surface } : {}),
      ...props,
    })
  },

  // Affiliate link clicked (inline, footer, or callout)
  affiliateLinkClicked: (
    articleSlug: string,
    productName: string,
    affiliateLink: string,
    placement: AffiliatePlacement,
    asin?: string | null,
    extra?: EventProperties
  ) => {
    const resolvedAsin = asin ?? extractAsin(affiliateLink)
    trackEvent('affiliate_link_clicked', {
      ...extra,
      article_slug: articleSlug,
      product_name: productName,
      affiliate_link: affiliateLink,
      placement,
      asin: resolvedAsin ?? undefined,
    })
  },

  /**
   * Unified affiliate telemetry across EVERY product surface (blog callouts,
   * guide/tool/city roundups). `surface` names where the card rendered so clicks
   * and impressions are comparable in one query and CTR is computable. Emitted
   * ALONGSIDE the legacy per-surface events (affiliate_link_clicked /
   * calculator_product_clicked) so historical funnels stay intact.
   */
  affiliateProductShown: (surface: string, props?: EventProperties) => {
    trackEvent('affiliate_product_shown', { surface, ...props })
  },
  affiliateProductClicked: (
    surface: string,
    opts: {
      productName: string
      affiliateLink: string
      asin?: string | null
      // Additive segmentation (e.g. lead-vs-body slot, product source). Forwarded
      // verbatim so callers can enrich CTR without touching this signature per surface.
      [key: string]: string | number | boolean | null | undefined
    },
  ) => {
    const { productName, affiliateLink, asin, ...rest } = opts
    trackEvent('affiliate_product_clicked', {
      surface,
      product_name: productName,
      affiliate_link: affiliateLink,
      asin: asin ?? extractAsin(affiliateLink) ?? undefined,
      ...rest,
    })
  },

  // Table of Contents opened (mobile drawer)
  tocOpened: (articleSlug: string, readingProgressPercent: number, sectionCount: number) => {
    trackEvent('toc_opened', {
      article_slug: articleSlug,
      reading_progress_percent: Math.round(readingProgressPercent),
      section_count: sectionCount
    })
  },

  // Table of Contents section clicked
  tocSectionClicked: (articleSlug: string, sectionId: string, sectionPosition: number, readingProgressPercent: number) => {
    trackEvent('toc_section_clicked', {
      article_slug: articleSlug,
      section_id: sectionId,
      section_position: sectionPosition,
      reading_progress_percent: Math.round(readingProgressPercent)
    })
  },

  // Article shared
  articleShared: (articleSlug: string, shareMethod: 'native' | 'clipboard', success: boolean) => {
    trackEvent('article_shared', {
      article_slug: articleSlug,
      share_method: shareMethod,
      success
    })
  }
}

export const toolNavigationAnalytics = {
  /** Fires once when a RelatedTools section enters the viewport / mounts */
  relatedToolsViewed: (props: {
    sourceTool: string
    placement: string
    toolCount: number
  }) => {
    trackEvent('tool_related_links_viewed', {
      source_tool: props.sourceTool,
      placement: props.placement,
      tool_count: props.toolCount,
    })
  },

  /** Fires on the receiving tool when the user arrived via a RelatedTools link */
  referredVisit: (props: {
    targetTool: string
    fromTool: string
  }) => {
    trackEvent('tool_referred_visit', {
      target_tool: props.targetTool,
      from_tool: props.fromTool,
    })
  },

  /** Fires when a user clicks a related tool link */
  relatedToolClicked: (props: {
    sourceTool: string
    targetTool: string
    rank: number
    placement: string
    recommendationReason: string
    prefillFields: string
  }) => {
    trackEvent('tool_related_link_clicked', {
      source_tool: props.sourceTool,
      target_tool: props.targetTool,
      rank: props.rank,
      placement: props.placement,
      recommendation_reason: props.recommendationReason,
      prefill_fields: props.prefillFields,
    })
  },
}

export const retentionAnalytics = {
  carePlanPersonalized: (props: {
    has_soil_test: boolean
    journal_steps_done_count: number
    disease_boosted_count: number
    account_age_days?: number
    lawn_age_days?: number
    total_journal_entries?: number
    user_tier: 'anonymous' | 'free' | 'premium'
  }) => {
    trackEvent('care_plan_personalized', props)
  },

  lawnScoreObserved: (props: {
    score: number
    label: string
    task_completion: number
    journal_consistency: number
    application_tracking: number
    account_age_days?: number
  }) => {
    trackEvent('lawn_score_observed', props)
  },

  returningEngagedSession: (props: {
    account_age_days: number
    lawns_count: number
    days_since_signup: number
    surface: string
  }) => {
    trackEvent('returning_engaged_session', props)
  },
}

export const communityAnalytics = {
  /**
   * Impression for the contextual discovery card, keyed on the same `source`
   * its CTA clicks carry so PostHog can compute a real per-mount CTR. Without
   * it the click events had no denominator — "shown often, converts badly" and
   * "barely renders" were indistinguishable (same gap `cta_surface_shown`
   * closed for the blog CTAs, 2026-07-22). Shape matches the
   * useImpressionEvent `emit` override: (source, props).
   */
  discoveryCardShown: (source: string, props?: EventProperties) => {
    trackEvent('community_discovery_card_shown', { source, ...props })
  },

  galleryCtaClicked: (context: CommunityDiscoveryContext) => {
    stashCommunityDiscoveryAttribution(context)
    trackEvent('community_gallery_cta_clicked', {
      source: context.source,
      mode: context.mode,
      entry_path: context.entryPath,
      grass_type: context.grassType || undefined,
      problem: context.problem || undefined,
      plan_type: context.planType,
      outcome: context.outcome,
    })
  },

  submissionCtaClicked: (context: CommunityDiscoveryContext) => {
    trackEvent('community_submission_cta_clicked', {
      source: context.source,
      mode: context.mode,
      entry_path: context.entryPath,
      grass_type: context.grassType || undefined,
      problem: context.problem || undefined,
      plan_type: context.planType,
      outcome: context.outcome,
    })
  },

  galleryViewed: () => {
    trackEvent('community_gallery_viewed')
  },

  filtersChanged: (filters: {
    sortBy: string
    grassType?: string
    region?: string
  }) => {
    trackEvent('community_filters_changed', {
      sort_by: filters.sortBy,
      grass_type: filters.grassType,
      region: filters.region,
    })
  },

  photoOpened: (photoId: string, surface: 'gallery_card' | 'gallery_quick_view' | 'detail' | 'leaderboard') => {
    trackEvent('community_photo_opened', {
      photo_id: photoId,
      surface,
    })
  },

  shareCompleted: (photoId: string, platform: string) => {
    trackEvent('community_photo_shared', {
      photo_id: photoId,
      platform,
    })
  },

  shareOpened: (photoId: string, platform: string) => {
    trackEvent('community_share_opened', {
      photo_id: photoId,
      platform,
    })
  },

  reportSubmitted: (photoId: string, reason: string) => {
    trackEvent('community_photo_reported', {
      photo_id: photoId,
      reason,
    })
  },

  // Community photo submitted
  photoSubmitted: (
    grassType: string,
    userTier: 'free' | 'premium',
    hasStatusEmail = false,
    attribution?: { source?: CommunityDiscoverySource },
  ) => {
    trackEvent('community_photo_submitted', {
      grass_type: grassType,
      user_tier: userTier,
      has_status_email: hasStatusEmail,
      source: attribution?.source,
    })
  },

  // Vote cast
  voteCast: (
    voteType: 'upvote' | 'downvote',
    photoId: string,
    action?: 'added' | 'changed' | 'removed',
  ) => {
    trackEvent('community_vote_cast', {
      vote_type: voteType,
      photo_id: photoId,
      action,
    })
  },

  // Leaderboard viewed
  leaderboardViewed: (userRank?: number) => {
    trackEvent('community_leaderboard_viewed', {
      user_rank: userRank
    })
  }
}

// ============================================
// TIER 3: TECHNICAL & PRODUCT METRICS
// ============================================

// Browser-extension and benign-noise error signatures that flood PostHog Error
// Tracking without representing real product bugs. Matched (case-insensitively)
// against the error message and, where available, the script filename.
//
// Why message-matching and not just filename: extension content-scripts execute
// in the page context, so the errors they throw are attributed to OUR Next.js
// chunk filenames — filename `.includes('extension')` misses them entirely. The
// top offender by far is Chrome's `runtime.sendMessage()` ("Tab not found"),
// which alone produced ~230 of the last 14 days' exceptions. The insertBefore /
// removeChild DOMExceptions are the canonical Google-Translate signature: a
// translation extension mutates the DOM out from under React's reconciler.
const IGNORABLE_ERROR_SIGNATURES = [
  'runtime.sendmessage',
  'tab not found',
  'extension context invalidated',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'resizeobserver loop', // benign layout-timing quirk, never user-visible
  // DOM-mutating extensions (Google Translate, etc.) racing React reconciliation:
  "failed to execute 'insertbefore' on 'node'",
  "failed to execute 'removechild' on 'node'",
  'is not a child of this node',
  'the object can not be found here',
  // styled-jsx tearing down a <style> element whose parent React already
  // detached during commitDeletionEffects — surfaces as a frameless
  // `TypeError: Cannot read properties of null (reading 'removeChild')` whose
  // ONLY app-distinguishing signal is the styled-jsx frame source (the message
  // is too generic to match safely). Matched here against `frame.source` via
  // before_send's isIgnorableExceptionEvent; styled-jsx's runtime only appears
  // in stacks for its own style inject/remove ops, never in app logic, so this
  // can't swallow a real bug. Recurring framework race, not user-visible.
  // (PostHog issue, 3 users / 24h, 2026-06.)
  'styled-jsx',
  // ...and the SAME race seen client-side at before_send, where the raw
  // posthog-js frame carries only `frame.function` (e.g. `r.deleteRule`), not
  // the server-symbolicated `source`, so the 'styled-jsx' token above never
  // matches there. styled-jsx's StyleSheet proto survives minification as
  // `<n>.deleteRule`; match it directly. No app code calls deleteRule, so this
  // can't swallow a real bug. This is what was actually leaking the "Cannot read
  // properties of null (reading 'removeChild')" issue (22 users / 8 days, 2026-06)
  // despite the 'styled-jsx' signature being present.
  'deleterule',
  // Outlook / Office "SafeLink" email-scanner webview — pure bot noise, our
  // single highest-volume non-extension signature (~140 / 30d).
  'object not found matching id',
  // Firefox internal probe injected into the page, never our code.
  '__firefox__',
  // PostHog's own session-recorder script failing to fetch (network blip).
  'posthog-recorder',
  // Google Maps internal module loader, fires from /maps-api-v3, not our bundle.
  // Module names vary ("util", "onion", etc.), so match the stable prefix.
  'could not load "',
  'symbrowser_modifywindowopenwithtarget',
  'darkreader',
  // "ParityDigital" coupon/shopping extension injects a content script that
  // iterates a non-array and throws `t().filter is not a function ... 'Parity-
  // Digital'===...`. Attributed to our analytics chunk because the extension
  // runs in page context, but it is not our code. (PostHog issue, 2026-06.)
  'paritydigital',
  "can't find variable: _g",
  'unsafe-eval',
  'was released because another request stole it',
  'lock was stolen by another request',
  // Stack-overflow RangeError on iOS Safari (JavaScriptCore). On long, deeply
  // nested blog posts JSC sits closer to its (smaller-than-V8) stack ceiling;
  // an overflow originating in a third-party / recorder script then tips the
  // capture path itself over while it serializes the stack, producing a
  // frameless `RangeError: Maximum call stack size exceeded` that fires in
  // bursts. There are no usable frames to act on, it is not reproducible off
  // iOS, and feeding it back through captureException is what amplifies it.
  // Drop it at every chokepoint. (PostHog issue D4, 2026-06.)
  'maximum call stack size exceeded',
  // React hydration mismatches (#418 hydration failed, #423 recovered-from-
  // error). Investigated 2026-07-07: 48 occurrences / 44 users over 2 weeks,
  // exclusively on content-heavy blog pages, never reproducible in dev, spread
  // across Chrome/Edge/Android including headless-Linux visitors hitting
  // brand-new posts within hours of publish (crawlers executing JS). Every
  // date render in the blog template is timeZone-pinned and no component uses
  // render-time randomness — the mismatches come from externally modified DOM
  // (Google Translate, reader modes, extensions, bot renderers). React recovers
  // by client-rendering, so users see a working page. If a REAL hydration bug
  // ships, it shows up in dev consoles and e2e runs, not only here.
  'minified react error #418',
  'minified react error #423',
  // Injected shopping/cashback SDK (Rakuten / Capital One Shopping / Honey-style
  // Safari Web Extension, or a rewards app's in-app browser) reading
  // `getInstance().store.cashback.supported` off an undefined singleton. The
  // content script runs in the page context, so the throw is attributed to our
  // Next.js chunk and caught by the global handler (mechanism.handled=true) with
  // no usable app frames. No WGIS code has a `cashback` concept, so this token
  // can't swallow a real bug. (PostHog issue 019f5224, 1 user iOS Safari, 2026-07.)
  'store.cashback',
  // Injected crypto-wallet extension / in-app browser writing to its own
  // `window.ethereum` global. Single 'global code' frame with no sourcemap, i.e.
  // a script injected into the page rather than a bundle of ours. WGIS ships no
  // web3 code and never references `window.ethereum` (grepped 2026-08-21, zero
  // hits outside this list), so this token can't swallow a real bug.
  // (PostHog issue 01a01b73, 1 occurrence / 1 user Mobile Safari iOS on a
  // /result/ page, 2026-08-19.)
  'window.ethereum',
  // Injected privacy-browser / embed content script (seen from DuckDuckGo iOS)
  // rejecting with an `invalid origin` postMessage/origin check. The throw is
  // attributed to a stale/404'd chunk and caught by the global unhandledrejection
  // handler (mechanism.handled=true) with no usable app frames. Grepped 2026-07-12:
  // no WGIS source throws this string, so it can't swallow a real bug.
  // (PostHog issue 019f56c3, 1 user DuckDuckGo iOS, 2026-07.)
  'invalid origin',
  // Grow.me (Mediavine) ad stack failing to load one of its own hashed ES
  // modules. Investigated 2026-07-25: 209 occurrences / 206 users in 9 days,
  // our single largest live issue. It is not our code —
  //   - zero stack frames and `source: null`, i.e. a cross-origin module;
  //   - 100% Safari (iOS + macOS). This exact wording is Safari's; Chrome says
  //     "Failed to fetch dynamically imported module";
  //   - fires on every page type, matching the site-wide `faves.grow.me/main.js`
  //     tag in app/layout.tsx, whose sibling failures (`UID2 SDK failed to load`,
  //     from grow.me's `uid2SdkLoader-<hash>.js`) appeared the same week;
  //   - one occurrence per user, i.e. a load failure, not a loop.
  // Production HTML ships no `type="module"` script of ours, and our own lazy
  // chunks fail as `ChunkLoadError`, so this token cannot swallow a WGIS bug.
  // It reaches us only because the `unhandledrejection` handler in
  // app/providers.tsx forwards every rejection, including third-party ones.
  // (PostHog issue 019f6e97, 2026-07.)
  'importing a module script failed',
  // The sibling failure named above: Grow.me's uid2SdkLoader retrying
  // cdn.prod.uidapi.com and rejecting when an ad/privacy blocker kills the
  // request. CSP verified 2026-07-25 (next.config.mjs allowlists
  // cdn.prod.uidapi.com + cdn.prod.euid.eu), so surviving events are pure
  // client-side blocking of a third-party identity SDK — nothing we control,
  // and Grow.me health is monitored by ad revenue, not this exception.
  // (8 of the 11 /diagnose $exception events in the 30d ending 2026-07-25.)
  'uid2 sdk failed to load',
  // Our CSP deliberately omits 'unsafe-eval' (see next.config.mjs), so anything
  // calling `Function()` is injected third-party code — an extension or an ad
  // vendor — never ours. The existing 'unsafe-eval' token above misses this
  // phrasing because the message never contains that string. Kept to the exact
  // observed signature rather than a bare 'blocked by csp' so a genuine
  // first-party CSP violation still surfaces.
  // (PostHog issue 019efb38, 6 occurrences / 2 users, 2026-07.)
  'call to function() blocked by csp',
  // Mediavine/Journey ad wrapper + Optable module promise rejections
  // (~1,300/day on blog pages, all mechanism.handled=true, error_source=
  // 'promise', stack frames pointing at scripts.journeymv.com/tags/optable/*
  // and scripts.scriptwrapper.com/tags/*. Loaded site-wide from app/layout.tsx;
  // no WGIS code ships from these hosts, so these tokens can't swallow a real
  // bug. (2026-07-31.)
  'journeymv.com',
  'scriptwrapper.com',
  // Safari's 'Load failed' lives in IGNORABLE_EXACT_MESSAGES below, not here:
  // as a substring it also matches our own "Upload failed (500)".
  // Firefox's wording for the same generic fetch failure, completing the trio
  // alongside Safari's 'Load failed'. Chrome's "Failed to fetch" stays
  // unfiltered as the cross-browser canary. Safe as a substring: unlike
  // 'load failed', no first-party message embeds this sentence.
  // Investigated 2026-08-14: 351 occurrences / 15 users in 7 days, the entire
  // exception spike for the period. It is not our code —
  //   - 100% Firefox (152 Windows, 115 macOS, 83 Android, 1 Linux), zero
  //     Chrome and zero Safari, which is how a browser-specific phrasing plus
  //     Firefox's default Enhanced Tracking Protection presents;
  //   - zero stack frames: `$exception_list` carries no `stacktrace` key at
  //     all, i.e. a cross-origin script, the same signature as 'load failed';
  //   - `error_source: promise`, so it reaches us only via the
  //     unhandledrejection handler in app/providers.tsx, which forwards
  //     third-party rejections;
  //   - repeats on a ~29s metronome within a single session, matching an ad
  //     refresh timer rather than a user action, and scales with dwell time
  //     (179 events from 2 sessions on /tools/lawn-size-calculator, 75 from 1
  //     session on a long blog guide) across every ad-bearing page type.
  // Our own fetch failures surface with frames and distinct messages ("Upload
  // failed (500)" etc.), and our lazy chunks fail as ChunkLoadError, so this
  // token cannot swallow a WGIS bug.
  // (PostHog issue 019e41e3, 2026-08-14.)
  'networkerror when attempting to fetch resource',
  // WebKit's promise-rejection phrasings from the same Grow.me / Mediavine ad
  // stack: frameless rejections with no stack, reaching us only through the
  // unhandledrejection handler in app/providers.tsx. Matched on the FULL
  // message rather than a bare 'aborterror' because our own fetchWithTimeout
  // (lib/network-utils.ts) and lib/api-retry.ts raise genuine AbortErrors that
  // must still surface, and Chrome's phrasings ("The user aborted a request",
  // "signal is aborted without reason") stay unfiltered so a real cross-browser
  // abort bug is still visible. (2026-08.)
  'aborterror: the operation was aborted',
  'aborterror: fetch is aborted',
  'notreadableerror: the i/o read operation failed',
  // The Chromium/Firefox wording of the Grow.me module failure already filtered
  // above as Safari's 'importing a module script failed'. Their phrasing embeds
  // the URL, so scope the token to the grow.me host rather than the generic
  // sentence — a real failure to import one of OUR modules must still surface.
  // Deliberately NOT a bare 'faves.grow.me': that token would also match the
  // `blockedURI` the securitypolicyviolation handler in app/providers.tsx feeds
  // through this same function, silencing the one signal that detects a CSP
  // block of the ad tag. (PostHog issue 019fe822, 2026-08-09.)
  'error loading dynamically imported module: https://faves.grow.me',
  // iOS in-app browser (Instagram / Facebook / etc.) tearing its WKWebView down
  // while a postMessage is still in flight. A platform teardown race in the host
  // app, not our code, and unreachable from a normal Safari tab — the user has
  // already left the page by the time it fires. (PostHog issue 019fe8be, 2026-08-09.)
  'wkwebview was deallocated',
  // Two more phrasings of the same iOS webview-bridge teardown race, plus
  // Android WebView's equivalent ("Java object is gone"): the host app's native
  // bridge disappears mid-postMessage. Web code never calls
  // window.webkit.messageHandlers — that API only exists inside webviews — so
  // none of these can swallow a first-party bug. (PostHog issues 019fd7b9,
  // 01a01128, 01a0115b, 2026-08.)
  'did not respond to this postmessage',
  'window.webkit.messagehandlers',
  'java object is gone',
  // Ad-consent framework probes from the Grow.me/Mediavine stack (its bundled
  // app.<hash>.js polls for a TCF/GPP consent-management platform and throws
  // when none answers). Grepped 2026-08-18: no WGIS source mentions either
  // acronym. (PostHog issues 019fe0fa, 01a00227, 2026-08.)
  'gpp cmp not found',
  'tcf api not found',
  // Grow.me serves the same uid2SdkLoader module from scripts.grow.me as well
  // as faves.grow.me (token above). Same CSP caveat: keep the full
  // 'error loading...' prefix so the securitypolicyviolation handler's
  // blockedURI still surfaces a CSP block of the tag. (PostHog issue 01a0066f,
  // 2026-08.)
  'error loading dynamically imported module: https://scripts.grow.me',
  // Grow.me's rebranded (EU) identity SDK alongside the 'uid2 sdk failed to
  // load' token above — same loader, same ad/privacy-blocker cause.
  // (PostHog issue 019ff006, 2026-08-11.)
  'euid sdk failed to load',
]

// Noise that can only be matched by full equality, because as a substring it
// would swallow real errors. Two kinds land here: messages that are a single
// minified identifier ('ga' is inside "message", "image", "propagate",
// "garbage"...), and short phrases that appear inside a longer first-party
// message ('load failed' is inside our own "Upload failed (500)").
// Compared against the message alone, never the stack or filename. A leading
// `<Name>Error: ` prefix is stripped first, so "TypeError: Load failed" and a
// bare "Load failed" both match.
const IGNORABLE_EXACT_MESSAGES = new Set([
  // The ad-consent stack (GPP / Grow.me) throwing a minified variable name.
  // Observed on an iOS Safari blog page one second after 'GPP CMP not found'
  // in the same session, handled=true, zero stack frames, source-less. Grepped
  // 2026-08-09: no WGIS source throws a bare two-letter message.
  // (PostHog issue 019fe726, 1 user, 2026-08-09.)
  'ga',
  // Same class as 'ga' above: an injected third-party script throwing a minified
  // two-letter variable name. Here the injector is Google Translate — every
  // occurrence is on a proxied blog page (whatgrassisthis-com.translate.goog,
  // English→Spanish) under Chrome iOS, handled=true, and the only stack frames
  // are our own capture path (app/providers.tsx global handler → captureError).
  // Grepped 2026-08-21: no WGIS source throws a bare 'Ba'/'ba' message.
  // (PostHog issue 01a01a70, 2 occurrences / 1 user, 2026-08-19.)
  'ba',
  // Safari's generic fetch-failure TypeError from the Grow.me / Mediavine ad
  // loads — frameless (no stack), so only the message can match. Observed as
  // exactly "Load failed" with nothing appended, 1,184 events / 60d, entirely
  // WebKit (Mobile Safari, Safari, Chrome iOS, Firefox iOS).
  //
  // Lives here rather than in IGNORABLE_ERROR_SIGNATURES because "upload
  // failed" contains "load failed": as a substring token it also silenced our
  // own `throw new Error('Upload failed (<status>)')` on the grass-ID upload
  // path (hooks/use-homepage-analysis.ts). Those events stopped reaching
  // PostHog on 2026-07-31, the day the substring token shipped, after a steady
  // ~1/month before it. Full-equality matching keeps Safari's noise filtered
  // while letting every "Upload failed (...)" through.
  //
  // Chrome's equivalent phrasing ("Failed to fetch") stays unfiltered as the
  // cross-browser canary; Firefox's is in IGNORABLE_ERROR_SIGNATURES above.
  // (2026-07-31; scoped to exact match 2026-08-14.)
  'load failed',
  // JavaScriptCore's own internal-failure message: 100% Mobile Safari, only on
  // ad-bearing blog pages, zero stack frames, handled=true, and it double-fires
  // within the same second — a JSC/WebKit fault, not an app throw. Exact-match
  // because our soil-temperature API returns `{ error: "Internal error" }` JSON;
  // its one consumer (national-soil-map.tsx) setState's the message rather than
  // throwing, but a future consumer that throws `Error("Internal error (500)")`
  // or similar must still surface. (PostHog issue 019fb808, 2026-08-18.)
  'internal error',
])

// Google Places' new API reports canceled/handled XHR work as an RpcError with
// code 6, across every RPC on the endpoint (AutocompletePlaces while the user
// types, GetPlace when they pick a suggestion). Ad and privacy blockers kill
// the same XHR. Both call sites in the lawn-size combobox catch it, clear stale
// results, and fall back to manual map navigation, so sending every vendor
// rejection to Error Tracking creates hundreds of copies without adding
// actionable signal. Keep the match deliberately narrow: only code 6, only XHR
// failures. Other Places RPC codes and other failure modes still flow to
// PostHog.
export const isHandledGooglePlacesRpcError = (haystack: string): boolean => {
  const normalized = haystack.toLowerCase()
  return (
    normalized.includes('rpc failed due to xhr error') &&
    normalized.includes('places.googleapis.com/$rpc/google.maps.places.v1.places/') &&
    /error code:\s*6(?:\D|$)/.test(normalized)
  )
}

/**
 * True when an error is browser-extension / cross-origin noise we don't want in
 * PostHog Error Tracking. Shared by the global window/promise handlers and the
 * React ErrorBoundary so all client exception paths filter identically.
 */
export const isIgnorableClientError = (
  message: string | undefined | null,
  filename?: string | null,
  stack?: string | null
): boolean => {
  if (message === 'Script error.') return true // cross-origin script, no stack

  // Exact-message check first, on the message by itself. Strip a leading
  // `<Name>Error: ` prefix so the same token matches whether the value arrives
  // raw (posthog's $exception_values) or already stringified by a handler.
  const bareMessage = (message ?? '').trim().toLowerCase().replace(/^[a-z]*error:\s*/, '')
  if (IGNORABLE_EXACT_MESSAGES.has(bareMessage)) return true

  const haystack = `${message ?? ''} ${filename ?? ''} ${stack ?? ''}`.toLowerCase()
  if (!haystack.trim()) return false
  return (
    isHandledGooglePlacesRpcError(haystack) ||
    IGNORABLE_ERROR_SIGNATURES.some((sig) => haystack.includes(sig))
  )
}

// Set while a captureError → posthog.captureException call is on the stack, so
// a stack-overflow thrown by the serialization can't re-enter via the global
// window `error` handler. See the note in captureError below. (PostHog D4.)
let captureInFlight = false

export const performanceAnalytics = {
  // API response time
  apiResponseTime: (endpoint: string, durationMs: number, status: 'success' | 'error') => {
    trackEvent('api_response_time', {
      endpoint,
      duration_ms: durationMs,
      status
    })
  },

  /**
   * Comprehensive error tracking with full context for debugging
   * Best practice: capture enough info to reproduce and fix
   */
  captureError: (options: {
    error: Error | unknown
    source: ClientErrorSource
    severity: 'fatal' | 'error' | 'warning'
    componentStack?: string
    context?: Record<string, string | number | boolean | undefined>
  }) => {
    if (typeof window === 'undefined') return

    // Re-entrancy guard. `posthog.captureException` below synchronously
    // serializes the error + walks its stack. When the original error is a
    // stack-overflow (`RangeError: Maximum call stack size exceeded`) the
    // process runs with the stack already at the ceiling — on iOS Safari /
    // JavaScriptCore that serialization can itself overflow and throw a second
    // RangeError, which the global window `error` handler then routes straight
    // back into captureError, amplifying one overflow into a burst of frameless
    // exceptions. This flag makes the capture path strictly non-reentrant: if a
    // capture is already in flight on this tick, drop the nested one instead of
    // recursing. (PostHog issue D4, 2026-06.)
    if (captureInFlight) return
    captureInFlight = true

    try {
      const { error, source, severity, componentStack, context } = options

      // Extract error details safely. Some libraries (heic2any, etc.) reject
      // with a plain object like `{ code: number, message: string }` rather than
      // an Error instance. `new Error(String(obj))` would stringify to the
      // useless "[object Object]" — unwrap `.message` first before falling back.
      const errorMsg =
        error instanceof Error
          ? undefined
          : typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message?: unknown }).message === 'string'
            ? (error as { message: string }).message
            : String(error)
      const errorObj = error instanceof Error ? error : new Error(errorMsg)

      // A rejection/throw with no actionable payload — `Promise.reject()`,
      // `throw undefined`, or an aborted fetch rejecting with no reason —
      // reaches us as a non-Error whose stringified message is empty or one of
      // "undefined" / "null" / "[object Object]". We'd otherwise manufacture a
      // frameless `Error: undefined` from it: pure noise in Error Tracking with
      // no message, no stack, and nothing to act on. Drop it. The
      // `unhandledrejection` handler in app/providers.tsx (which forwards
      // event.reason verbatim) is the main source. (PostHog "Error: undefined".)
      if (!(error instanceof Error)) {
        const normalized = errorMsg?.trim().toLowerCase()
        // `[object object]` is the plain-object case; the general `[object <Type>]`
        // shape also catches a thrown DOM value — an image/FileReader `Event` or
        // `ProgressEvent` bubbling out of the compression/upload path stringifies
        // to `[object Event]` / `[object ProgressEvent]` with no `.message` to act
        // on. Real Errors never reach this branch, so dropping the whole shape is
        // safe. (PostHog issue 019f5622 "[object Event]", 1 user Android, 2026-07.)
        if (
          !normalized ||
          normalized === 'undefined' ||
          normalized === 'null' ||
          /^\[object [a-z]+\]$/.test(normalized)
        ) {
          return
        }
      }

      // Drop browser-extension / cross-origin noise before it reaches Error
      // Tracking. Explicit Maps captures bypass this so APIProvider can preserve
      // referrer/quota loader failures while global window/promise noise is still
      // suppressed. The stack-overflow signature lives in the shared list, so it
      // is filtered here too — belt-and-suspenders with the re-entrancy guard.
      const shouldApplyNoiseFilter = !source.startsWith('google-maps-')
      const stack = [errorObj.stack, componentStack].filter(Boolean).join('\n')
      if (shouldApplyNoiseFilter && isIgnorableClientError(errorObj.message, undefined, stack)) return

      if (!posthog) return

      try {
        posthog.captureException(errorObj, {
          error_source: source,
          error_severity: severity,
          page_url: window.location.href.slice(0, 200),
          page_path: window.location.pathname,
          component_stack: componentStack?.slice(0, 300),
          ...context
        })
      } catch (captureErr) {
        console.error('Failed to capture exception to PostHog:', captureErr)
      }
    } finally {
      captureInFlight = false
    }
  }
}

export const featureAnalytics = {
  // Feature discovered
  featureDiscovered: (feature: string, userTier: 'free' | 'premium') => {
    trackEvent('feature_discovered', {
      feature,
      user_tier: userTier
    })
  },

  // PDF export used (available to signed-in Free and Premium accounts)
  pdfExportUsed: (contentType: 'calculator_results' | 'care_plan', userTier: 'free' | 'premium') => {
    trackEvent('pdf_export_used', {
      content_type: contentType,
      user_tier: userTier
    })
  }
}

export const arcadeAnalytics = {
  // Game card clicked on the /games arcade hub
  gameCardClicked: (game: 'lawn-space-shooter' | 'lawn-stripe-studio', hasBestScore: boolean, dailyPattern?: string) => {
    trackEvent('arcade_game_card_clicked', {
      game,
      has_best_score: hasBestScore,
      daily_pattern: dailyPattern
    })
  },

  // Daily pattern chip clicked (deep-links Stripe Studio with today's pattern)
  dailyPatternClicked: (pattern: string) => {
    trackEvent('arcade_daily_pattern_clicked', { pattern })
  },

  // "Challenge a friend" share affordance used
  challengeShared: (method: 'web-share' | 'clipboard') => {
    trackEvent('arcade_challenge_shared', { method })
  },

  // Whimsy grass strip mowed on the hub (fired once per regrowth cycle)
  grassStripMowed: (totalBladesTrimmed: number) => {
    trackEvent('arcade_grass_strip_mowed', { total_blades_trimmed: totalBladesTrimmed })
  }
}
